// src/pages/ClockIn.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db, storage, auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { getOrg } from "../utils/tenant";
import { invalidateStaffCache, loadStaffForKiosk } from "../utils/loadStaff";
import { invalidateRolesCache, loadRolesForKiosk } from "../utils/loadRoles";
import { useBoundStoreIdentity } from "../hooks/useBoundStore";
import useAuthReady from "../hooks/useAuthReady";
import { useTheme } from "../theme/ThemeProvider";
import useResolvedBusinessMode from "../hooks/useResolvedBusinessMode";
import { BUSINESS_MODES } from "../utils/businessMode";

import { formatDurationMinutes } from "../utils/durationFormat";
import {
  cacheLocalOpenShift,
  clearLocalOpenShift,
  enqueueAttendanceOperation,
  flushAttendanceOutbox,
  getLocalOpenShift,
  makeAttendanceId,
  syncAttendanceOperationNow,
} from "../utils/attendanceOutbox";
import { checkForNeoUpdate, forceNeoAppRefresh, NEO_BUILD_ID } from "../registerSW";
import { dedupePayrollRows, getDisplayClockOutSec } from "../utils/payrollShiftEngine";
import LiveClockInCamera from "./LiveClockInCamera";
/* ---------- tiny inline toast ---------- */
function InlineToast({ message, type = "info", onClose }) {
  if (!message) return null;

  const bg =
    type === "error"
      ? "#fee2e2"
      : type === "success"
      ? "#dcfce7"
      : "#e5f3ff";

  const fg =
    type === "error"
      ? "#991b1b"
      : type === "success"
      ? "#065f46"
      : "#0b3b8c";

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={onClose}
      style={{
        position: "fixed",
        left: "50%",
        bottom: 16,
        transform: "translateX(-50%)",
        zIndex: 50,
        maxWidth: 520,
        width: "calc(100% - 32px)",
        background: bg,
        color: fg,
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 8px 22px rgba(16,24,40,.18)",
        padding: "10px 12px",
        borderRadius: 10,
        cursor: "pointer",
        textAlign: "center",
      }}
      title="Click to dismiss"
    >
      {message}
    </div>
  );
}

/* ---------------- helpers ---------------- */
const tsToDate = (v) => {
  if (v instanceof Date) return v;
  if (v?.toDate) return v.toDate();
  if (typeof v?.seconds === "number") return new Date(v.seconds * 1000);
  if (typeof v === "number" && Number.isFinite(v)) {
    return new Date(v > 1e12 ? v : v * 1000);
  }
  if (typeof v === "string" && v.trim()) {
    const ms = Date.parse(v);
    if (Number.isFinite(ms)) return new Date(ms);
  }
  return null;
};

const shiftClockInDate = (shift) =>
  tsToDate(shift?.clockIn) ||
  tsToDate(shift?.clockInAt) ||
  tsToDate(shift?.in) ||
  tsToDate(shift?.inAt) ||
  null;

const safeHours = (start, end = new Date()) => {
  const from = tsToDate(start);
  const to = tsToDate(end);
  // Missing/invalid clock-in data must never look like a fresh 0-hour shift.
  // That was the source of false "already clocked in" states from malformed
  // legacy active documents.
  if (!from || !to) return Number.POSITIVE_INFINITY;
  const diff = to.getTime() - from.getTime();
  return diff >= 0 ? diff / 3600000 : Number.POSITIVE_INFINITY;
};

const titleCase = (s = "") =>
  s
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const getOrgId = () => getOrg()?.orgId || localStorage.getItem("orgId") || "";
const shiftsColl = (orgId, storeId) =>
  collection(db, "orgs", orgId, "stores", storeId, "shifts");

const DEFAULT_ABANDONED_SHIFT_HOURS = 18;
const MIN_ABANDONED_SHIFT_HOURS = 18;
const MAX_ABANDONED_SHIFT_HOURS = 24;

const clampAbandonedShiftHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_ABANDONED_SHIFT_HOURS;
  return Math.min(
    MAX_ABANDONED_SHIFT_HOURS,
    Math.max(MIN_ABANDONED_SHIFT_HOURS, Math.round(n))
  );
};

const resolveAbandonedShiftHours = async (orgId, storeId) => {
  try {
    const [storeSnap, laborSnap] = await Promise.all([
      getDoc(doc(db, "orgs", orgId, "stores", storeId)),
      getDoc(doc(db, "orgs", orgId, "settings", "labor")),
    ]);

    const storeData = storeSnap.exists() ? storeSnap.data() || {} : {};
    const laborData = laborSnap.exists() ? laborSnap.data() || {} : {};
    // Settings > Auto clock-out is the shared source of truth. Prefer the
    // organisation Labor policy so stale store documents cannot postpone a
    // force-close. Store values remain a backwards-compatible fallback.
    return clampAbandonedShiftHours(
      laborData?.forceAutoClockOutHours ??
        laborData?.abandonedShiftHours ??
        storeData?.forceAutoClockOutHours ??
        storeData?.abandonedShiftHours ??
        DEFAULT_ABANDONED_SHIFT_HOURS
    );
  } catch {
    return DEFAULT_ABANDONED_SHIFT_HOURS;
  }
};

const isShiftClosed = (shift) =>
  Boolean(
    tsToDate(shift?.clockOut) ||
      tsToDate(shift?.actualClockOut) ||
      tsToDate(shift?.thresholdClockOut) ||
      shift?.active === false
  );

const isOpenShift = (shift) => !isShiftClosed(shift);

const pickLatestLiveOpenShift = (
  docs,
  abandonedShiftHours,
  now = new Date()
) => {
  const hoursLimit = clampAbandonedShiftHours(abandonedShiftHours);

  return (docs || [])
    .map((d) => ({ id: d.id, ...(d.data ? d.data() : d) }))
    .filter((shift) => {
      if (!isOpenShift(shift)) return false;
      const clockIn = shiftClockInDate(shift);
      if (!clockIn) return false;
      return safeHours(clockIn, now) <= hoursLimit;
    })
    .sort(
      (a, b) =>
        (shiftClockInDate(b)?.getTime?.() || 0) -
        (shiftClockInDate(a)?.getTime?.() || 0)
    )[0] || null;
};

const getThresholdClockOutDate = (shift, abandonedShiftHours) => {
  const clockIn = shiftClockInDate(shift);
  if (!clockIn) return null;
  const hours = clampAbandonedShiftHours(abandonedShiftHours);
  return new Date(clockIn.getTime() + hours * 3600000);
};

const closeShiftAtThreshold = async (
  orgId,
  storeId,
  shift,
  abandonedShiftHours,
  now = new Date()
) => {
  if (!shift?.id || isShiftClosed(shift)) return false;

  const thresholdClockOutDate = getThresholdClockOutDate(
    shift,
    abandonedShiftHours
  );
  if (!thresholdClockOutDate || now <= thresholdClockOutDate) return false;

  const clockInDate = shiftClockInDate(shift);
  const diffMs = clockInDate
    ? thresholdClockOutDate.getTime() - clockInDate.getTime()
    : 0;
  const totalMin = Math.max(0, Math.floor(diffMs / 60000));
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;

  await updateDoc(doc(shiftsColl(orgId, storeId), shift.id), {
    clockOut: Timestamp.fromDate(thresholdClockOutDate),
    actualClockOut: Timestamp.fromDate(now),
    thresholdClockOut: Timestamp.fromDate(thresholdClockOutDate),
    autoClockedOutAtThreshold: true,
    active: false,
    duration: formatDurationMinutes(hrs * 60 + mins),
  });

  try {
    await addDoc(collection(db, "orgs", orgId, "clockins"), {
      orgId,
      storeId,
      name: shift.name || "",
      role: shift.role || "",
      type: "auto_out",
      ts: Timestamp.fromDate(now),
      thresholdTs: Timestamp.fromDate(thresholdClockOutDate),
    });
  } catch {}

  return true;
};

const resolveLiveOpenShift = async (orgId, storeId, userName) => {
  const [snap, abandonedShiftHours] = await Promise.all([
    getDocs(query(
      shiftsColl(orgId, storeId),
      where("name", "==", userName),
      where("active", "==", true)
    )),
    resolveAbandonedShiftHours(orgId, storeId),
  ]);

  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const now = new Date();

  for (const shift of docs) {
    await closeShiftAtThreshold(orgId, storeId, shift, abandonedShiftHours, now);
  }

  const refreshedSnap = await getDocs(
    query(
      shiftsColl(orgId, storeId),
      where("name", "==", userName),
      where("active", "==", true)
    )
  );

  const validLiveRows = refreshedSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((shift) => {
      const clockIn = shiftClockInDate(shift);
      return isOpenShift(shift) && !!clockIn && safeHours(clockIn, now) <= abandonedShiftHours;
    });

  return {
    abandonedShiftHours,
    activeShiftCount: validLiveRows.length,
    liveOpenShift: pickLatestLiveOpenShift(
      refreshedSnap.docs,
      abandonedShiftHours,
      now
    ),
  };
};

const sweepStoreAbandonedShifts = async (orgId, storeId) => {
  const abandonedShiftHours = await resolveAbandonedShiftHours(orgId, storeId);
  // Only active shifts can need a force-close. Never download the store's
  // historical shift collection just to enforce the safety threshold.
  const snap = await getDocs(
    query(shiftsColl(orgId, storeId), where("active", "==", true))
  );
  const now = new Date();

  for (const d of snap.docs) {
    await closeShiftAtThreshold(
      orgId,
      storeId,
      { id: d.id, ...d.data() },
      abandonedShiftHours,
      now
    );
  }
};


/* ---- live Daily Message ---- */
function useDailyMessage(storeId) {
  const [text, setText] = useState("");
  const { ready: authReady, signedIn } = useAuthReady();
  const orgId = getOrgId();

  useEffect(() => {
    if (!orgId || !authReady || !signedIn) return;

    const refAll = doc(db, "orgs", orgId, "dailyMessages", "all");
    const refStore = storeId
      ? doc(db, "orgs", orgId, "dailyMessages", `store_${storeId}`)
      : null;

    let allText = "";
    let storeText = "";
    const unsubs = [];

    unsubs.push(
      onSnapshot(refAll, (snap) => {
        const d = snap.exists() ? snap.data() || {} : {};
        allText = ((d.text ?? d.message ?? "") + "").trim();
        setText(storeText || allText);
      })
    );

    if (refStore) {
      unsubs.push(
        onSnapshot(refStore, (snap) => {
          const d = snap.exists() ? snap.data() || {} : {};
          storeText = ((d.text ?? d.message ?? "") + "").trim();
          setText(storeText || allText);
        })
      );
    }

    return () => unsubs.forEach((u) => u && u());
  }, [orgId, storeId, authReady, signedIn]);

  return text;
}

const neoDateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const neoMonthKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const getMonthScheduleDates = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dates = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    dates.push(neoDateKey(new Date(year, month, day, 12, 0, 0, 0)));
  }
  return dates;
};

const shiftLabelForSchedule = (entry = {}) => {
  if (!entry) return "Not scheduled";
  if (entry.isAttendance === true || entry.attendanceTypeId) {
    return entry.attendanceLabel || entry.leaveLabel || "Leave / attendance";
  }
  if (entry.off === true || String(entry.publishedShift || "").toUpperCase() === "OFF") {
    return "OFF";
  }
  const published = String(entry.publishedShift || entry.shift || "").trim();
  if (published) return published.replace(/-/g, " – ");
  if (entry.startTime && entry.endTime) return `${entry.startTime} – ${entry.endTime}`;
  return "Not scheduled";
};

const scheduleWindowForKiosk = (entry = {}) => {
  const published = String(entry?.publishedShift || entry?.shift || "").trim();
  const match = published.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  return {
    startTime: entry?.startTime || match?.[1] || "",
    endTime: entry?.endTime || match?.[2] || "",
  };
};

const kioskScheduleDateTime = (dateKey, timeText) => {
  const d = String(dateKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t = String(timeText || "").match(/^(\d{2}):(\d{2})$/);
  if (!d || !t) return null;
  return new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]), 0, 0);
};

const kioskShiftDateKey = (shift = {}) => {
  const date = tsToDate(shift?.clockIn) ||
    (Number(shift?.clockInMs) ? new Date(Number(shift.clockInMs)) : null);
  return date ? neoDateKey(date) : "";
};

const kioskClockLabel = (value) => {
  const date = tsToDate(value) || (Number(value) ? new Date(Number(value)) : null);
  if (!date || Number.isNaN(date.getTime())) return "—";

  // Neo Schedule Intelligence uses a 24-hour clock while retaining
  // AM/PM context for actual attendance timestamps (e.g. 17:31 PM).
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const period = date.getHours() >= 12 ? "PM" : "AM";
  return `${hours}:${minutes} ${period}`;
};

const kioskDurationLabel = (hours = 0) => {
  const minutes = Math.max(0, Math.round((Number(hours) || 0) * 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
};

const getKioskShiftLunchMinutes = (shift = {}, fallbackMinutes = 60) => {
  const direct = Number(shift?.lunchMin ?? shift?.lunchMinutes ?? shift?.lunch);
  if (Number.isFinite(direct) && direct >= 0) return direct;
  if (typeof shift?.lunchTaken === "boolean") return shift.lunchTaken ? fallbackMinutes : 0;
  return fallbackMinutes;
};

const dedupeKioskShifts = (items = [], abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS) =>
  dedupePayrollRows(items || [], (shift) => getDisplayClockOutSec(shift, abandonedShiftHours))
    .sort((a, b) => (tsToDate(a?.clockIn)?.getTime?.() || 0) - (tsToDate(b?.clockIn)?.getTime?.() || 0));

const kioskAttendanceHours = (entry = {}, defaultLunchMinutes = 60) => {
  const direct = [entry.allocatedHours, entry.paidHours, entry.scheduledHours, entry.hours, entry.durationHours, entry.leaveHours, entry.attendanceHours]
    .map(Number)
    .find((value) => Number.isFinite(value) && value >= 0);
  if (direct !== undefined) return direct;
  const win = scheduleWindowForKiosk(entry);
  let start = kioskScheduleDateTime(entry?.date, win.startTime);
  let end = kioskScheduleDateTime(entry?.date, win.endTime);
  if (!start || !end) return 0;
  if (end <= start) end = new Date(end.getTime() + 86400000);
  return Math.max(0, (end - start) / 3600000 - Math.max(0, Number(defaultLunchMinutes) || 0) / 60);
};

const buildKioskDayIntelligence = ({ entry = null, shifts = [], dateKey = "", now = new Date(), defaultLunchMinutes = 60 }) => {
  const isToday = dateKey === neoDateKey(now);
  const selectedDate = dateKey ? new Date(`${dateKey}T12:00:00`) : null;
  const isFuture = selectedDate && selectedDate > new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const dayShifts = (shifts || []).filter((shift) => kioskShiftDateKey(shift) === dateKey)
    .sort((a, b) => (tsToDate(a?.clockIn)?.getTime?.() || Number(a?.clockInMs) || 0) - (tsToDate(b?.clockIn)?.getTime?.() || Number(b?.clockInMs) || 0));
  const shift = dayShifts[dayShifts.length - 1] || null;
  const isAttendance = Boolean(entry?.isAttendance === true || entry?.attendanceTypeId);
  const isOff = Boolean(entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF");

  if (isAttendance) {
    const label = entry?.attendanceLabel || entry?.leaveLabel || "Leave / attendance";
    const paid = !(String(`${entry?.attendanceTypeId || ""} ${label}`).toLowerCase().includes("unpaid") || entry?.paid === false || entry?.isPaid === false);
    const hours = paid ? kioskAttendanceHours(entry, defaultLunchMinutes) : 0;
    return {
      status: label,
      tone: "#a78bfa",
      actual: "No clock-in expected",
      scheduledPaid: hours,
      credited: hours,
      creditLabel: paid ? `${kioskDurationLabel(hours)} paid leave credit` : "Unpaid leave · 0h credited",
      decision: paid ? "Approved paid attendance" : "Approved unpaid attendance",
    };
  }

  if (isOff && !shift) {
    return { status: "Off day", tone: "#94a3b8", actual: "No clock-in expected", scheduledPaid: 0, credited: 0, creditLabel: "0h scheduled · 0h credited", decision: "Published OFF day" };
  }

  if (!entry && !shift) {
    return { status: isFuture ? "Not scheduled" : "No schedule / attendance", tone: "#94a3b8", actual: "No attendance recorded", scheduledPaid: 0, credited: 0, creditLabel: "No credited time", decision: "—" };
  }

  const win = scheduleWindowForKiosk(entry || {});
  let scheduledStart = kioskScheduleDateTime(dateKey, win.startTime);
  let scheduledEnd = kioskScheduleDateTime(dateKey, win.endTime);
  if (scheduledStart && scheduledEnd && scheduledEnd <= scheduledStart) scheduledEnd = new Date(scheduledEnd.getTime() + 86400000);
  const fullScheduledHours = scheduledStart && scheduledEnd
    ? Math.max(0, (scheduledEnd - scheduledStart) / 3600000 - defaultLunchMinutes / 60)
    : 0;

  if (!shift) {
    if (isFuture || (scheduledStart && now < scheduledStart)) {
      return { status: "Upcoming", tone: "#60a5fa", actual: "Not started yet", scheduledPaid: fullScheduledHours, credited: 0, creditLabel: `${kioskDurationLabel(fullScheduledHours)} scheduled paid time`, decision: "Published shift" };
    }
    return { status: "No attendance recorded", tone: "#fb7185", actual: "No clock-in found", scheduledPaid: fullScheduledHours, credited: 0, creditLabel: `0h credited of ${kioskDurationLabel(fullScheduledHours)} scheduled`, decision: "Attendance missing" };
  }

  const clockIn = tsToDate(shift?.clockIn) || (Number(shift?.clockInMs) ? new Date(Number(shift.clockInMs)) : null);
  let clockOut = tsToDate(shift?.clockOut || shift?.actualClockOut || shift?.thresholdClockOut) || (Number(shift?.clockOutMs) ? new Date(Number(shift.clockOutMs)) : null);
  const live = !clockOut && isToday;
  const effectiveOut = live ? now : clockOut;
  const approved = shift?.scheduleVarianceApproved === true;
  const rejected = shift?.scheduleVarianceRejected === true;
  const exceptionType = String(shift?.scheduleVarianceType || "").replace(/_/g, " ").trim();

  let credited = 0;
  if (clockIn && effectiveOut && effectiveOut > clockIn) {
    if (!entry || isOff) {
      if (approved) {
        credited = Math.max(0, (effectiveOut - clockIn) / 3600000 - (live ? 0 : defaultLunchMinutes / 60));
      }
    } else if (scheduledStart && scheduledEnd) {
      const creditStart = approved && clockIn < scheduledStart ? clockIn : new Date(Math.max(clockIn.getTime(), scheduledStart.getTime()));
      const rosterEndToNow = new Date(Math.min(scheduledEnd.getTime(), now.getTime()));
      const creditEnd = approved && effectiveOut > scheduledEnd
        ? new Date(Math.min(effectiveOut.getTime(), now.getTime()))
        : new Date(Math.min(effectiveOut.getTime(), rosterEndToNow.getTime()));
      if (creditEnd > creditStart) credited = Math.max(0, (creditEnd - creditStart) / 3600000 - (live ? 0 : defaultLunchMinutes / 60));
    }
  }

  let status = live ? "On shift" : "Completed";
  let tone = "#34d399";
  const isOvertimeException = exceptionType.toLowerCase().includes("overtime");
  const overtimeHours = isOvertimeException && scheduledEnd && effectiveOut && effectiveOut > scheduledEnd
    ? (effectiveOut - scheduledEnd) / 3600000
    : 0;
  const varianceStatusLabel = `${overtimeHours > 0 ? `${kioskDurationLabel(overtimeHours)} ` : ""}${exceptionType || "Schedule exception"}`;
  if (approved) { status = `${varianceStatusLabel} · approved`; tone = "#34d399"; }
  else if (rejected) { status = `${varianceStatusLabel} · rejected`; tone = "#fb7185"; }
  else if (isOff || !entry) { status = live ? "Working outside roster · review pending" : "Unscheduled work · review pending"; tone = "#fbbf24"; }
  else if (clockIn && scheduledStart) {
    const lateMinutes = Math.round((clockIn - scheduledStart) / 60000);
    if (lateMinutes > 0) { status = `${lateMinutes}m late${live ? " · on shift" : ""}`; tone = "#fbbf24"; }
  }

  const actual = clockIn
    ? `${kioskClockLabel(clockIn)} → ${live ? "On shift" : kioskClockLabel(clockOut)}`
    : "No attendance recorded";
  const decision = approved ? "Manager approved" : rejected ? "Manager rejected" : exceptionType ? "Pending manager review" : (live ? "Live attendance" : "No exception decision");

  return { status, tone, actual, scheduledPaid: fullScheduledHours, credited, creditLabel: `${kioskDurationLabel(credited)} credited${fullScheduledHours ? ` of ${kioskDurationLabel(fullScheduledHours)} scheduled` : ""}`, decision };
};


const buildKioskMonthIntelligence = ({
  entries = [],
  shifts = [],
  now = new Date(),
  defaultLunchMinutes = 60,
  abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS,
  activeShiftId = "",
}) => {
  const monthKey = neoMonthKey(now);
  const todayKey = neoDateKey(now);
  const entriesByDate = new Map();
  (entries || []).forEach((entry) => {
    const dateKey = String(entry?.date || "");
    if (dateKey.startsWith(monthKey)) entriesByDate.set(dateKey, entry);
  });

  let publishedMonthHours = 0;
  let scheduledToDate = 0;
  let creditedWorkedToDate = 0;
  let paidLeaveToDate = 0;

  const monthDates = [];
  const cursor = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  while (cursor <= monthEnd) {
    monthDates.push(neoDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  monthDates.forEach((dateKey) => {
    const entry = entriesByDate.get(dateKey) || null;
    const isAttendance = Boolean(entry?.isAttendance === true || entry?.attendanceTypeId);
    const isOff = Boolean(entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF");

    if (isAttendance) {
      const label = entry?.attendanceLabel || entry?.leaveLabel || "";
      const paid = !(String(`${entry?.attendanceTypeId || ""} ${label}`).toLowerCase().includes("unpaid") || entry?.paid === false || entry?.isPaid === false);
      const leaveHours = paid ? kioskAttendanceHours(entry, defaultLunchMinutes) : 0;
      publishedMonthHours += leaveHours;
      if (dateKey <= todayKey) {
        scheduledToDate += leaveHours;
        paidLeaveToDate += leaveHours;
      }
      return;
    }
    if (isOff || !entry) return;

    const win = scheduleWindowForKiosk(entry);
    let start = kioskScheduleDateTime(dateKey, win.startTime);
    let finish = kioskScheduleDateTime(dateKey, win.endTime);
    if (!start || !finish) return;
    if (finish <= start) finish = new Date(finish.getTime() + 86400000);

    const fullRaw = Math.max(0, (finish - start) / 3600000);
    const fullPaid = Math.max(0, fullRaw - Math.max(0, Number(defaultLunchMinutes) || 0) / 60);
    publishedMonthHours += fullPaid;

    if (now <= start) return;
    if (now < finish) scheduledToDate += Math.max(0, (now - start) / 3600000);
    else scheduledToDate += fullPaid;
  });

  // Match Staff Portal/Labor: evaluate every deduped physical shift. Choosing one
  // row per date breaks staff who have multiple attendance/exception rows in a day.
  const physicalShifts = dedupeKioskShifts(shifts || [], abandonedShiftHours);
  physicalShifts.forEach((shift) => {
    const clockIn = tsToDate(shift?.clockIn) || (Number(shift?.clockInMs) ? new Date(Number(shift.clockInMs)) : null);
    if (!clockIn || neoMonthKey(clockIn) !== monthKey || clockIn > now) return;

    const dateKey = neoDateKey(clockIn);
    const entry = entriesByDate.get(dateKey) || null;
    const explicitOut = tsToDate(shift?.clockOut || shift?.actualClockOut || shift?.thresholdClockOut) ||
      (Number(shift?.clockOutMs) ? new Date(Number(shift.clockOutMs)) : null);
    const isLive = !explicitOut && (
      (activeShiftId && String(shift?.id || "") === String(activeShiftId)) ||
      shift?.active === true || String(shift?.status || "").toLowerCase() === "open"
    );
    let actualEnd = isLive ? now : explicitOut;
    if (!actualEnd) {
      const effectiveOutSec = getDisplayClockOutSec(shift, abandonedShiftHours);
      actualEnd = typeof effectiveOutSec === "number" ? new Date(effectiveOutSec * 1000) : null;
    }
    if (!actualEnd || actualEnd <= clockIn) return;

    const approved = shift?.scheduleVarianceApproved === true;
    const isAttendance = Boolean(entry?.isAttendance === true || entry?.attendanceTypeId);
    const isOff = Boolean(entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF");

    if (!entry || isOff || isAttendance) {
      if (!approved) return;
      const raw = Math.max(0, (actualEnd - clockIn) / 3600000);
      const lunch = isLive ? 0 : getKioskShiftLunchMinutes(shift, defaultLunchMinutes) / 60;
      creditedWorkedToDate += Math.max(0, raw - lunch);
      return;
    }

    const win = scheduleWindowForKiosk(entry);
    let scheduledStart = kioskScheduleDateTime(dateKey, win.startTime);
    let scheduledEnd = kioskScheduleDateTime(dateKey, win.endTime);
    if (!scheduledStart || !scheduledEnd) return;
    if (scheduledEnd <= scheduledStart) scheduledEnd = new Date(scheduledEnd.getTime() + 86400000);

    const creditStart = approved && clockIn < scheduledStart
      ? clockIn
      : new Date(Math.max(clockIn.getTime(), scheduledStart.getTime()));
    const rosterEndToNow = new Date(Math.min(scheduledEnd.getTime(), now.getTime()));
    const creditEnd = approved && actualEnd > scheduledEnd
      ? new Date(Math.min(actualEnd.getTime(), now.getTime()))
      : new Date(Math.min(actualEnd.getTime(), rosterEndToNow.getTime()));
    if (creditEnd <= creditStart) return;

    const raw = (creditEnd - creditStart) / 3600000;
    const lunch = isLive ? 0 : getKioskShiftLunchMinutes(shift, defaultLunchMinutes) / 60;
    creditedWorkedToDate += Math.max(0, raw - lunch);
  });

  const creditedToDate = creditedWorkedToDate + paidLeaveToDate;
  const varianceHours = creditedToDate - scheduledToDate;
  const remainingScheduledHours = Math.max(0, publishedMonthHours - scheduledToDate);
  const projectedMonthEndHours = creditedToDate + remainingScheduledHours;
  return { publishedMonthHours, scheduledToDate, creditedToDate, varianceHours, remainingScheduledHours, projectedMonthEndHours };
};

const kioskSignedDurationLabel = (hours = 0) => {
  const value = Number(hours) || 0;
  const label = kioskDurationLabel(Math.abs(value));
  if (Math.abs(value) < 1 / 120) return "On schedule";
  return value > 0 ? `↑ ${label} ahead` : `↓ ${label} behind`;
};

/* ---------------- page ---------------- */
export default function ClockIn() {
  const { ready: authReady, signedIn } = useAuthReady();
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const isLight = themeMode === "light";
  const styles = useMemo(() => getStyles(themeMode), [themeMode]);
  const { businessMode } = useResolvedBusinessMode();
  const isSecurityMode = businessMode === BUSINESS_MODES.SECURITY_SERVICES;
  const [securityPhotoFile, setSecurityPhotoFile] = useState(null);
  const [securityPhotoPreview, setSecurityPhotoPreview] = useState("");
  const [liveCameraOpen, setLiveCameraOpen] = useState(false);
  // P1.2: site-scoped attendance policy. The shared iPad reads it once per site
  // and fails closed if a photo policy cannot be loaded.
  const [photoPolicy, setPhotoPolicy] = useState({ siteKey: "", required: false, outRequired: false, ready: false });

  const boundStore = useBoundStoreIdentity();
  const locationLabel = isSecurityMode ? "Site" : "Store";
  const storeName =
    boundStore.storeName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    locationLabel;

  const storeId = boundStore.storeId || "";
  const orgId = boundStore.orgId || getOrgId();
  const photoPolicyKey = `${orgId}/${storeId}`;
  const photoPolicyReady = photoPolicy.ready && photoPolicy.siteKey === photoPolicyKey;
  const photoRequiredForClockIn = isSecurityMode || (photoPolicyReady && photoPolicy.required);
  const photoRequiredForClockOut = photoPolicyReady && photoPolicy.outRequired;

  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState([]);

  const [selectedStaff, setSelectedStaff] = useState("");
  const [pin, setPin] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState({ today: null, tomorrow: null });
  const [selectedScheduleLoading, setSelectedScheduleLoading] = useState(false);
  const [staffScheduleOpen, setStaffScheduleOpen] = useState(false);
  const [staffScheduleLoading, setStaffScheduleLoading] = useState(false);
  const [staffScheduleRows, setStaffScheduleRows] = useState([]);
  const [staffScheduleShifts, setStaffScheduleShifts] = useState([]);
  const [staffScheduleIssues, setStaffScheduleIssues] = useState([]);
  const [staffScheduleSelectedDate, setStaffScheduleSelectedDate] = useState("");
  const [staffScheduleView, setStaffScheduleView] = useState("month");
  const [staffScheduleStaffName, setStaffScheduleStaffName] = useState("");
  const [staffScheduleStaffId, setStaffScheduleStaffId] = useState("");
  const [staffScheduleRole, setStaffScheduleRole] = useState("");
  const [scheduleIssueOpen, setScheduleIssueOpen] = useState(false);
  const [scheduleIssueReason, setScheduleIssueReason] = useState("");
  const [scheduleIssueNote, setScheduleIssueNote] = useState("");
  const [scheduleIssueSending, setScheduleIssueSending] = useState(false);
  const [scheduleIssueSentDate, setScheduleIssueSentDate] = useState("");

  const [isClockedIn, setIsClockedIn] = useState(false);
  const [openShiftId, setOpenShiftId] = useState(null);
  const [openShift, setOpenShift] = useState(null);

  const [confirmClockOut, setConfirmClockOut] = useState(false);
  const [showClockPanel, setShowClockPanel] = useState(false);
  const [clockActionBusy, setClockActionBusy] = useState(null);
  const clockActionLockRef = useRef(null);

  const [statusLine, setStatusLine] = useState("");
  const [refreshState, setRefreshState] = useState("idle");
  const [updateAvailable, setUpdateAvailable] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "info" });

  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const [now, setNow] = useState(new Date());

  const showToast = useCallback(
    (message, type = "info") => setToast({ message, type }),
    []
  );

  const dailyMessage = useDailyMessage(storeId);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      // Never block the clock UI on recovery. Flush durable attendance writes
      // in the background as soon as connectivity returns.
      flushAttendanceOutbox().catch(() => {});
    };
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    flushAttendanceOutbox().catch(() => {});
    const retryTimer = window.setInterval(() => {
      flushAttendanceOutbox().catch(() => {});
    }, 15000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(retryTimer);
    };
  }, []);

  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: "", type: "info" }), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let cancelled = false;

    const syncUpdate = async () => {
      const result = await checkForNeoUpdate({ autoRefreshSafeRoute: false });
      if (!cancelled) {
        setUpdateAvailable(
          result?.available
            ? { latestBuild: String(result.latestBuild || "") }
            : null
        );
      }
    };

    const handleUpdateAvailable = (event) => {
      const latestBuild = String(event?.detail?.latestBuild || "");
      setUpdateAvailable({ latestBuild });
    };

    window.addEventListener("neo:update-available", handleUpdateAvailable);
    syncUpdate().catch(() => {});

    return () => {
      cancelled = true;
      window.removeEventListener("neo:update-available", handleUpdateAvailable);
    };
  }, []);

  useEffect(() => {
    if (!orgId || !storeId) {
      navigate("/select-store", { replace: true });
    }
  }, [orgId, storeId, navigate]);

  useEffect(() => {
    if (!orgId || !storeId) return;
    let cancelled = false;
    const siteKey = `${orgId}/${storeId}`;
    // Keep the last verified policy for short offline restarts. The backend
    // remains authoritative when queued clock-ins eventually synchronise.
    const cacheKey = `neo.photoPolicy.v1.${orgId}.${storeId}`;
    let cachedPolicy = null;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (cached && typeof cached.required === "boolean" &&
          Number(cached.readAtMs) > Date.now() - 6 * 60 * 60 * 1000) cachedPolicy = cached;
    } catch (_) {}
    setPhotoPolicy({ siteKey, required: cachedPolicy?.required === true, outRequired: cachedPolicy?.outRequired === true, ready: !!cachedPolicy });
    // One on-demand site-document read; no listener or periodic polling.
    getDoc(doc(db, "orgs", orgId, "stores", storeId))
      .then((snap) => {
        if (cancelled) return;
        const required = snap.data()?.neoAttendancePhotoRequired === true;
        const outRequired = snap.data()?.neoAttendancePhotoOutRequired === true;
        setPhotoPolicy({ siteKey, required, outRequired, ready: true });
        try { localStorage.setItem(cacheKey, JSON.stringify({ required, outRequired, readAtMs: Date.now() })); } catch (_) {}
      })
      .catch((error) => {
        console.error("[kiosk] photo attendance policy unavailable", error);
        if (!cancelled && !cachedPolicy) setPhotoPolicy({ siteKey, required: false, outRequired: false, ready: false });
      });
    return () => { cancelled = true; };
  }, [orgId, storeId]);

  useEffect(() => {
    // A relink must never carry a selected staff member/role from the old store.
    setSelectedStaff("");
    setSelectedRole("");
    setPin("");
    setIsClockedIn(false);
    setOpenShiftId(null);
    setOpenShift(null);
    setSecurityPhotoFile(null);
    setSecurityPhotoPreview("");
    setLiveCameraOpen(false);
  }, [orgId, storeId]);

  const loadLists = useCallback(async () => {
    if (!orgId || !storeId) return;

    let loadedUsers = [];

    try {
      await sweepStoreAbandonedShifts(orgId, storeId);
    } catch (e) {
      console.error("[kiosk] sweep abandoned shifts failed:", e);
    }

    try {
      const users = await loadStaffForKiosk(orgId, storeId, storeName);
      loadedUsers = [...(users || [])];
      setStaffList(
        [...loadedUsers].sort((a, b) =>
          String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
            sensitivity: "base",
          })
        )
      );
    } catch (e) {
      console.error("[kiosk] load staff failed:", e);
      setStaffList([]);
    }

    try {
      const r = await loadRolesForKiosk(orgId, storeId);

      // Settings > Roles is the source of truth.
      // Do NOT re-introduce archived/renamed roles from stale staff records.
      const cleaned = Array.from(
        new Set((r || []).map((x) => titleCase(String(x || ""))))
      )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      setRoles(cleaned);
    } catch (e) {
      console.error("[kiosk] load roles failed:", e);
      setRoles([]);
    }
  }, [orgId, storeId, storeName]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshState("refreshing");
      invalidateStaffCache(orgId, storeId);
      invalidateRolesCache(orgId, storeId);

      await loadLists();

      const update = await checkForNeoUpdate({ autoRefreshSafeRoute: false });
      const latestBuild = update?.latestBuild || updateAvailable?.latestBuild || "";

      setRefreshState("done");

      // This is intentionally a true application-shell refresh, not only a data
      // refresh. It gives the installed PWA the same result as Safari's Reload.
      window.setTimeout(() => {
        forceNeoAppRefresh(latestBuild).catch(() => {
          window.location.reload();
        });
      }, update?.available ? 250 : 450);
    } catch {
      setRefreshState("idle");
    }
  }, [loadLists, orgId, storeId, updateAvailable]);

  useEffect(() => {
    if (!orgId || !storeId || !selectedStaff) {
      setIsClockedIn(false);
      setOpenShiftId(null);
      setOpenShift(null);
      return;
    }

    // iPad-first: trust the last locally confirmed action immediately.
    // Firestore reconciliation continues in the background.
    const cached = getLocalOpenShift(orgId, storeId, selectedStaff);
    if (cached?.id) {
      setOpenShiftId(cached.id);
      setOpenShift(cached);
      setIsClockedIn(true);
    } else {
      setOpenShiftId(null);
      setOpenShift(null);
      setIsClockedIn(false);
    }

    let cancelled = false;
    (async () => {
      try {
        const { liveOpenShift, abandonedShiftHours } = await resolveLiveOpenShift(
          orgId,
          storeId,
          selectedStaff
        );
        if (cancelled) return;

        if (liveOpenShift?.id) {
          const cachedShift = {
            id: liveOpenShift.id,
            name: liveOpenShift.name || selectedStaff,
            role: liveOpenShift.role || "",
            clockInMs: shiftClockInDate(liveOpenShift)?.getTime?.() || Date.now(),
            abandonedShiftHours,
          };
          cacheLocalOpenShift(orgId, storeId, selectedStaff, cachedShift);
          setOpenShiftId(liveOpenShift.id);
          setOpenShift({ ...liveOpenShift, ...cachedShift });
          setIsClockedIn(true);
        } else {
          // Firestore is authoritative once the live check succeeds.
          // A locally cached shift can be stale when Neo's backend/manager workflow
          // auto-clocks somebody out on another device. In that case the kiosk cache
          // must not keep showing "Clock Out" indefinitely.
          clearLocalOpenShift(orgId, storeId, selectedStaff);
          setOpenShiftId(null);
          setOpenShift(null);
          setIsClockedIn(false);
        }
      } catch (e) {
        // Do not turn a locally clocked-in staff member back to "Clock In"
        // merely because the iPad's Wi-Fi timed out.
        console.error("clocked-in background check error:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, storeId, selectedStaff]);

  useEffect(() => {
    if (!orgId || !storeId || !selectedStaff) {
      setSelectedSchedule({ today: null, tomorrow: null });
      setSelectedScheduleLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSelectedScheduleLoading(true);

    (async () => {
      try {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const todayKey = neoDateKey(today);
        const tomorrowKey = neoDateKey(tomorrow);

        // Cost-optimised: one on-demand query for only today + tomorrow.
        // No listener, no monthly schedule download.
        const snap = await getDocs(
          query(
            collection(db, "orgs", orgId, "neo_schedule_entries"),
            // Phase 8: kiosk reads are store-scoped in Firestore rules.
            // Include the bound store in the query so Rules can prove every
            // returned schedule entry belongs to this kiosk.
            where("storeId", "==", storeId),
            where("date", "in", [todayKey, tomorrowKey])
          )
        );

        if (cancelled) return;

        const selectedUser = staffList.find(
          (u) => String(u?.name || "").trim().toLowerCase() === String(selectedStaff).trim().toLowerCase()
        );
        const selectedId = String(selectedUser?.id || "");
        const selectedName = String(selectedStaff).trim().toLowerCase();

        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => String(row.storeId || "") === String(storeId))
          .filter((row) => row.published === true && String(row.draftStatus || "") === "published")
          .filter((row) => {
            const rowId = String(row.staffId || row.userId || row.employeeId || "");
            const rowName = String(row.staffName || row.name || "").trim().toLowerCase();
            return (selectedId && rowId === selectedId) || (!!selectedName && rowName === selectedName);
          });

        const firstFor = (dateKey) => rows.find((row) => String(row.date || "") === dateKey) || null;
        setSelectedSchedule({
          today: firstFor(todayKey),
          tomorrow: firstFor(tomorrowKey),
        });
      } catch (error) {
        console.error("[kiosk] selected staff schedule preview failed:", error);
        if (!cancelled) setSelectedSchedule({ today: null, tomorrow: null });
      } finally {
        if (!cancelled) setSelectedScheduleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, storeId, selectedStaff, staffList]);

  const writeStaffSession = useCallback(
    (user, shiftDocId = null) => {
      const nowTs = Date.now();
      const hours = 16;
      const session = {
        type: "staff",
        name: user.name,
        role: selectedRole || user.role || "",
        storeId,
        storeName,
        orgId,
        startedAt: nowTs,
        expiresAt: nowTs + hours * 60 * 60 * 1000,
        shiftDocId,
      };
      try {
        sessionStorage.setItem("staffSession", JSON.stringify(session));
        localStorage.setItem("cashup_staff_session", JSON.stringify(session));
        localStorage.setItem("staffSession", JSON.stringify(session));
        localStorage.setItem("session_staff", JSON.stringify(session));
        localStorage.setItem("cashup.session", JSON.stringify(session));
        localStorage.setItem("staffName", user.name);
        localStorage.setItem("cashup_staff_name", user.name);
        localStorage.setItem("staffRole", session.role || "");
      } catch {}
    },
    [selectedRole, storeId, storeName, orgId]
  );

  const clearStaffSession = () => {
    try {
      sessionStorage.removeItem("staffSession");
      localStorage.removeItem("cashup_staff_session");
      localStorage.removeItem("staffSession");
      localStorage.removeItem("session_staff");
      localStorage.removeItem("cashup.session");
      localStorage.removeItem("staffName");
      localStorage.removeItem("cashup_staff_name");
      localStorage.removeItem("staffRole");
    } catch {}
  };

  const resetForNext = useCallback(() => {
    setSelectedStaff("");
    setPin("");
    setSelectedRole("");
    setIsClockedIn(false);
    setOpenShiftId(null);
    setOpenShift(null);
    setConfirmClockOut(false);
    setShowClockPanel(false);
    clockActionLockRef.current = null;
    setClockActionBusy(null);
    setStatusLine("");
    setSecurityPhotoFile(null);
    setSecurityPhotoPreview("");
    setLiveCameraOpen(false);
  }, []);

  const handleStaffSelect = (name) => {
    setSelectedStaff(name);
    setPin("");
    setStatusLine("");
    setSecurityPhotoFile(null);
    setSecurityPhotoPreview("");
    setLiveCameraOpen(false);

    const u = staffList.find(
      (staff) => String(staff.name || "").trim().toLowerCase() === String(name || "").trim().toLowerCase()
    );
    const assignedRole = u?.role ? titleCase(u.role) : "";

    // Auto-select only when the assigned role is still active in Settings.
    // A legacy/renamed role must never be added back into the kiosk dropdown.
    const activeAssignedRole =
      roles.find(
        (role) =>
          String(role || "").trim().toLowerCase() ===
          assignedRole.trim().toLowerCase()
      ) || "";

    setSelectedRole(activeAssignedRole);

    const cached = getLocalOpenShift(orgId, storeId, name);
    setIsClockedIn(!!cached?.id);
    setOpenShiftId(cached?.id || null);
    setOpenShift(cached || null);
    setConfirmClockOut(false);
  };

  const onPinChange = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
    // A photograph belongs to the PIN verification that preceded it.
    // Changing the PIN must require a fresh capture.
    if (digits !== pin) {
      setSecurityPhotoFile(null);
      setSecurityPhotoPreview("");
      setLiveCameraOpen(false);
    }
    setPin(digits);
  };

  const getSelectedUser = () =>
    staffList.find(
      (u) =>
        String(u.name || "").trim().toLowerCase() ===
        String(selectedStaff || "").trim().toLowerCase()
    ) || null;

  const selectedUser = getSelectedUser();
  const isPinValid =
    !!selectedUser &&
    !!pin &&
    String(selectedUser.pin ?? "") === String(pin);
  const canCaptureSecurityPhoto =
    !!selectedStaff && (isClockedIn || !!selectedRole) && isPinValid && photoPolicyReady;

  const uploadAttendancePhoto = async (file, userName, eventId) => {
    if (!file) return null;

    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("Device auth not ready. Please wait a moment and try again.");
    }

    const tokenResult = await currentUser.getIdTokenResult(true);
    const claims = tokenResult?.claims || {};

    if (!claims?.orgId || claims.orgId !== orgId) {
      throw new Error("Device session is not linked correctly. Please relink this device.");
    }

    const safeName = String(userName || "staff").replace(/[^a-z0-9_-]+/gi, "-");
    const ext = (file.type || "image/jpeg").split("/").pop() || "jpg";
    // Binding the file to one unique attendance event blocks reuse of older
    // photos as evidence for a new event. The backend validates this prefix.
    const path = `orgs/${orgId}/stores/${storeId}/clockin-photos/${eventId}-${Date.now()}-${safeName}.${ext}`;
    const fileRef = storageRef(storage, path);

    try {
      await uploadBytes(fileRef, file, {
        contentType: file.type || "image/jpeg",
      });
    } catch (err) {
      throw err;
    }

    // No tokenised download URLs: photos are fetched by authorised Neo Admin users
    // directly through Firebase Storage rules using authenticated requests.
    return { path, contentType: file.type || "image/jpeg" };
  };

  const buildClockInOperation = (user, role, extraShiftData = {}, ids = {}) => {
    const nowMs = Date.now();
    const shiftId = ids.shiftId || makeAttendanceId("shift");
    const eventId = ids.eventId || makeAttendanceId("clockin");
    const localShift = {
      id: shiftId,
      name: user.name,
      staffId: user.id || "",
      role,
      clockInMs: nowMs,
      abandonedShiftHours: DEFAULT_ABANDONED_SHIFT_HOURS,
      pendingSync: true,
    };

    const operation = {
      type: "clock_in",
      shiftId,
      eventId,
      orgId,
      storeId,
      storeName,
      staffName: user.name,
      staffId: user.id || "",
      role,
      clockInMs: nowMs,
      extraShiftData,
      clockInPhotoUrl: extraShiftData.clockInPhotoUrl || "",
      clockInPhotoPath: extraShiftData.clockInPhotoPath || "",
    };

    return { localShift, operation };
  };

  const queueClockInForRetry = (localShift, operation) => {
    cacheLocalOpenShift(orgId, storeId, operation.staffName, localShift);
    enqueueAttendanceOperation(operation);
    flushAttendanceOutbox().catch(() => {});
    return localShift;
  };

  const isRetryableAttendanceError = (error) => {
    const code = String(error?.code || "").toLowerCase();
    return (
      code.includes("unavailable") ||
      code.includes("deadline-exceeded") ||
      code.includes("network-request-failed") ||
      code.includes("resource-exhausted")
    );
  };

  const handleClockIn = async () => {
    if (clockActionLockRef.current || clockActionBusy) return;
    setShowClockPanel(true);

    if (!selectedStaff || !selectedRole) {
      showToast("Please fill all fields.", "error");
      return;
    }

    const user = getSelectedUser();
    if (!user) {
      showToast("Please select a valid staff member.", "error");
      return;
    }

    if (!pin) {
      showToast(
        isSecurityMode ? "Enter your security PIN." : "Please enter PIN.",
        "error"
      );
      return;
    }

    if (String(user.pin) !== String(pin)) {
      showToast("Incorrect PIN.", "error");
      return;
    }

    if (!photoPolicyReady) {
      showToast("Attendance settings unavailable. Connect to the internet and reopen Neo.", "error");
      return;
    }
    if (photoRequiredForClockIn && !securityPhotoFile) {
      showToast("Take a clock-in photo after entering the correct PIN.", "error");
      return;
    }
    if (photoRequiredForClockIn && !online) {
      showToast("Photo clock-in requires internet. Please contact the site administrator.", "error");
      return;
    }

    clockActionLockRef.current = "in";
    setClockActionBusy("in");

    try {
      const cachedOpen = getLocalOpenShift(orgId, storeId, user.name);

      // Online clock-in authority lives on the backend. Do not let a stale
      // browser cache or a failed Firestore pre-check claim that somebody is
      // already clocked in. The callable below atomically decides whether an
      // open shift exists or whether a new shift may be created.
      //
      // Offline remains local-first: if this kiosk already has an unsynced/open
      // shift for the selected person, prevent a second offline clock-in.
      if (!online && cachedOpen?.id) {
        writeStaffSession(user, cachedOpen.id);
        setOpenShift(cachedOpen);
        setOpenShiftId(cachedOpen.id);
        setIsClockedIn(true);
        showToast(`${user.name} is already clocked in on this device.`, "info");
        setTimeout(resetForNext, 2200);
        return;
      }

      let extraShiftData = {};
      const ids = { shiftId: makeAttendanceId("shift"), eventId: makeAttendanceId("clockin") };
      if (photoRequiredForClockIn && securityPhotoFile) {
        // Photos must reach Storage before an attendance event can be accepted.
        // Never persist a File object to localStorage for offline retries.
        const uploadedPhoto = await uploadAttendancePhoto(
          securityPhotoFile, user.name, ids.eventId
        );
        if (!uploadedPhoto?.path) throw new Error("Photo upload failed. Please try again.");
        extraShiftData = {
          clockInPhotoPath: uploadedPhoto.path,
          clockInPhotoContentType: uploadedPhoto.contentType,
          clockInPhotoCapturedAt: Timestamp.now(),
        };
      }

      const pending = buildClockInOperation(user, selectedRole, extraShiftData, ids);
      let localShift = pending.localShift;
      let syncedOnline = false;

      if (online) {
        try {
          const result = await syncAttendanceOperationNow(pending.operation);
          syncedOnline = true;

          if (result?.status === "already_open" && result?.shift?.id) {
            const serverShift = {
              id: result.shift.id,
              name: result.shift.name || user.name,
              staffId: result.shift.staffId || user.id || "",
              role: result.shift.role || selectedRole || "",
              clockInMs: Number(result.shift.clockInMs || Date.now()),
              abandonedShiftHours: DEFAULT_ABANDONED_SHIFT_HOURS,
            };
            cacheLocalOpenShift(orgId, storeId, user.name, serverShift);
            writeStaffSession(user, serverShift.id);
            setOpenShift(serverShift);
            setOpenShiftId(serverShift.id);
            setIsClockedIn(true);
            showToast(
              Number(result.activeShiftCount || 0) > 1
                ? `${result.activeShiftCount} active shifts already exist for ${user.name}. A manager must resolve the duplicates.`
                : `${user.name} is already clocked in.`,
              Number(result.activeShiftCount || 0) > 1 ? "error" : "info"
            );
            setTimeout(resetForNext, 2200);
            return;
          }

          localShift = { ...localShift, pendingSync: false };
          cacheLocalOpenShift(orgId, storeId, user.name, localShift);
        } catch (error) {
          if (!isRetryableAttendanceError(error)) throw error;
          // If Wi-Fi drops between the pre-check and the atomic write, preserve
          // the offline-first behavior. The durable outbox will retry through
          // the same backend lock when connectivity returns.
          queueClockInForRetry(localShift, pending.operation);
        }
      } else {
        queueClockInForRetry(localShift, pending.operation);
      }

      writeStaffSession(user, localShift.id);
      setOpenShift(localShift);
      setOpenShiftId(localShift.id);
      setIsClockedIn(true);

      const t = new Date(localShift.clockInMs);
      setStatusLine(
        `✅ Clocked in — ${t.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}${syncedOnline ? "" : " · syncing when online"}`
      );
      showToast(
        syncedOnline ? "Clock in recorded ✓" : "Clock in saved ✓ · will sync when online",
        "success"
      );

      // Get the kiosk back to Lightspeed / the next person quickly.
      setTimeout(resetForNext, 1400);
    } catch (e) {
      console.error("Clock-in failed:", e);
      setStatusLine("");
      showToast(e?.message || "Failed to record clock-in. Please try again.", "error");
    } finally {
      clockActionLockRef.current = null;
      setClockActionBusy(null);
    }
  };

  const handleClockOutClick = () => {
    if (clockActionLockRef.current || clockActionBusy) return;
    setShowClockPanel(true);
    if (!selectedStaff) {
      showToast("Please select your name.", "error");
      return;
    }
    const user = getSelectedUser();
    if (!user) {
      showToast("Please select a valid staff member.", "error");
      return;
    }
    if (!pin) {
      showToast(
        isSecurityMode ? "Enter your security PIN to clock out." : "Please enter PIN.",
        "error"
      );
      return;
    }
    if (String(user.pin) !== String(pin)) {
      showToast("Incorrect PIN.", "error");
      return;
    }
    if (!photoPolicyReady) {
      showToast("Attendance settings unavailable. Reconnect and reopen Neo.", "error");
      return;
    }
    if (photoRequiredForClockOut && !securityPhotoFile) {
      showToast("Take a clock-out photo after entering the correct PIN.", "error");
      return;
    }
    if (photoRequiredForClockOut && !online) {
      showToast("Photo clock-out requires internet. Please contact your manager.", "error");
      return;
    }
    setConfirmClockOut(true);
  };

  const handleClockOut = async () => {
    if (clockActionLockRef.current || clockActionBusy) return;
    // Recheck the policy before upload: do not accept an unattended clock-out
    // through the confirmation dialog or a lost connection.
    if (!photoPolicyReady || (photoRequiredForClockOut && (!isPinValid || !securityPhotoFile || !online))) {
      setConfirmClockOut(false);
      showToast("A valid PIN, live photo and internet are required to clock out.", "error");
      return;
    }
    clockActionLockRef.current = "out";
    setClockActionBusy("out");

    try {
      const user = getSelectedUser();
      const cachedShift =
        openShift ||
        getLocalOpenShift(orgId, storeId, selectedStaff);

      let activeShift = cachedShift;
      let abandonedShiftHours =
        Number(cachedShift?.abandonedShiftHours || DEFAULT_ABANDONED_SHIFT_HOURS);

      // Legacy / cross-device shifts may not exist in this iPad's local cache.
      // Only those cases need to wait for Firestore discovery.
      if (!activeShift?.id) {
        const resolved = await resolveLiveOpenShift(orgId, storeId, selectedStaff);
        activeShift = resolved.liveOpenShift;
        abandonedShiftHours = resolved.abandonedShiftHours;
      }

      if (!activeShift?.id) {
        showToast("No active clock-in found.", "error");
        setConfirmClockOut(false);
        return;
      }

      const outMs = Date.now();
      const clockInMs =
        Number(activeShift.clockInMs || 0) ||
        tsToDate(activeShift.clockIn)?.getTime?.() ||
        0;

      const eventId = makeAttendanceId("clockout");
      const operation = {
        type: "clock_out",
        shiftId: activeShift.id,
        eventId,
        orgId,
        storeId,
        staffName: selectedStaff,
        staffId: user?.id || activeShift.staffId || "",
        role: selectedRole || activeShift.role || user?.role || "",
        clockInMs,
        clockOutMs: outMs,
        abandonedShiftHours,
      };
      let pendingSync = false;
      if (photoRequiredForClockOut) {
        // Upload first, then ask the authoritative backend to close the shift.
        // Do not silently mark a photo-required clock-out successful offline.
        const uploaded = await uploadAttendancePhoto(securityPhotoFile, user.name, eventId);
        if (!uploaded?.path) throw new Error("Clock-out photo upload failed. Please retry.");
        operation.clockOutPhotoPath = uploaded.path;
        operation.clockOutPhotoContentType = uploaded.contentType;
        operation.clockOutPhotoCapturedAtMs = Number(securityPhotoFile.lastModified) || outMs;
        try {
          const result = await syncAttendanceOperationNow(operation);
          // A stale local shift must not be reported as successfully clocked out.
          if (result?.status !== "closed") {
            throw new Error("This shift is no longer open. Refresh Neo and ask your manager to review the attendance record.");
          }
        } catch (error) {
          if (!isRetryableAttendanceError(error)) throw error;
          // Photo is already in Storage; retain this exact event for safe retry.
          enqueueAttendanceOperation(operation);
          pendingSync = true;
        }
      } else {
        // Preserve the existing offline-first experience for PIN-only sites.
        enqueueAttendanceOperation(operation);
      }

      // For photo-required sites, the backend has accepted the close, or an
      // uploaded-photo operation is explicitly pending retry. Legacy PIN-only
      // shifts retain their original optimistic close flow.
      clearLocalOpenShift(orgId, storeId, selectedStaff);
      setOpenShift(null);
      setOpenShiftId(null);
      setIsClockedIn(false);
      setConfirmClockOut(false);
      clearStaffSession();

      setStatusLine(
        `${pendingSync ? "⏳ Clock-out awaiting sync" : "✅ Clocked out"} — ${new Date(outMs).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}${pendingSync || !online ? " · syncing when online" : ""}`
      );
      showToast(
        pendingSync ? "Photo uploaded; clock-out pending confirmation. Keep this device online." :
          online ? (photoRequiredForClockOut ? "Photo clock-out verified ✓" : "Clock out saved ✓ · syncing") : "Clock out saved ✓ · will sync when online",
        pendingSync ? "info" : "success"
      );

      flushAttendanceOutbox().catch(() => {});
      setTimeout(resetForNext, 1400);
    } catch (e) {
      console.error("Clock out error:", e);
      setStatusLine("");
      showToast("Failed to record clock out.", "error");
      setConfirmClockOut(false);
    } finally {
      clockActionLockRef.current = null;
      setClockActionBusy(null);
    }
  };

  const openStaffSchedule = async () => {
    if (!selectedStaff || !pin) {
      showToast("Select your name and enter your PIN to view your schedule.", "error");
      return;
    }

    const user = getSelectedUser();
    if (!user || String(user.pin) !== String(pin)) {
      showToast(!user ? "Please select a valid staff member." : "Incorrect PIN.", "error");
      return;
    }

    const monthKey = neoMonthKey(new Date());
    setStaffScheduleStaffName(selectedStaff);
    setStaffScheduleStaffId(String(user?.id || user?.uid || user?.staffId || ""));
    setStaffScheduleRole(selectedRole || user?.roleName || user?.role || "");
    setScheduleIssueOpen(false);
    setScheduleIssueReason("");
    setScheduleIssueNote("");
    setScheduleIssueSentDate("");
    setStaffScheduleSelectedDate(neoDateKey(new Date()));
    setStaffScheduleRows([]);
    setStaffScheduleShifts([]);
    setStaffScheduleLoading(true);
    setStaffScheduleOpen(true);

    try {
      // SM2 keeps the drawer on-demand: one staff-scoped monthly schedule read,
      // plus one staff-scoped attendance history read when the drawer opens.
      // No realtime listener is added.
      const [scheduleSnap, shiftSnap, legacyShiftSnap, issueSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "orgs", orgId, "neo_schedule_entries"),
            where("monthKey", "==", monthKey),
            where("storeId", "==", storeId),
            where("staffName", "==", selectedStaff)
          )
        ),
        getDocs(
          query(
            shiftsColl(orgId, storeId),
            where("name", "==", selectedStaff)
          )
        ),
        // Keep Schedule Intelligence aligned with Staff Portal/Labor for
        // historical records that still exist in the legacy shift collection.
        getDocs(
          query(
            collection(db, "cashup_shifts"),
            where("orgId", "==", orgId),
            where("name", "==", selectedStaff)
          )
        ),
        getDocs(
          query(
            collection(db, "orgs", orgId, "neoScheduleIssues"),
            // Phase 8: schedule issues are site-scoped. Query the kiosk's
            // bound store explicitly so the read is provably within scope.
            // Staff filtering remains client-side below to avoid introducing
            // another composite index for this on-demand drawer.
            where("storeId", "==", storeId)
          )
        ),
      ]);

      const rows = scheduleSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((row) => row.published === true && String(row.draftStatus || "") === "published")
        .filter((row) => String(row.date || "").startsWith(monthKey))
        .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

      const normalizedStoreName = String(storeName || "").trim().toLowerCase();
      const nestedShifts = shiftSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}), __source: "nested" }));
      const legacyShifts = legacyShiftSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}), __source: "legacy" }))
        .filter((shift) => {
          const itemStoreId = String(shift?.storeId || shift?.store || "").trim();
          const itemStoreName = String(shift?.storeName || shift?.storeLabel || shift?.store || "").trim().toLowerCase();
          return itemStoreId === String(storeId) || itemStoreName === normalizedStoreName;
        });
      const monthShifts = dedupeKioskShifts([...nestedShifts, ...legacyShifts])
        .filter((shift) => kioskShiftDateKey(shift).startsWith(monthKey));

      const monthIssues = issueSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((issue) => String(issue.storeId || "") === String(storeId))
        .filter((issue) => String(issue.staffName || "").trim().toLowerCase() === String(selectedStaff || "").trim().toLowerCase())
        .filter((issue) => String(issue.monthKey || issue.date || "").startsWith(monthKey))
        .sort((a, b) => {
          const ad = String(a.date || "");
          const bd = String(b.date || "");
          if (ad !== bd) return bd.localeCompare(ad);
          const at = tsToDate(a.createdAt)?.getTime?.() || 0;
          const bt = tsToDate(b.createdAt)?.getTime?.() || 0;
          return bt - at;
        });

      setStaffScheduleRows(rows);
      setStaffScheduleShifts(monthShifts);
      setStaffScheduleIssues(monthIssues);
    } catch (error) {
      console.error("[kiosk] staff monthly schedule intelligence failed:", error);
      setStaffScheduleRows([]);
      setStaffScheduleShifts([]);
      setStaffScheduleIssues([]);
      showToast("Could not load your schedule intelligence.", "error");
    } finally {
      setStaffScheduleLoading(false);
    }
  };

  const submitScheduleIssue = async () => {
    if (!staffScheduleSelectedDate || !scheduleIssueReason || !staffScheduleStaffName) return;
    const row = staffScheduleRows.find(
      (entry) => String(entry.date || "") === String(staffScheduleSelectedDate || "")
    );
    if (!row) {
      showToast("That published shift could not be found.", "error");
      return;
    }
    setScheduleIssueSending(true);
    try {
      const createdIssue = {
        orgId,
        storeId,
        storeName,
        staffId: staffScheduleStaffId || "",
        staffName: staffScheduleStaffName,
        role: row?.roleName || row?.role || staffScheduleRole || "",
        date: staffScheduleSelectedDate,
        monthKey: neoMonthKey(new Date(`${staffScheduleSelectedDate}T12:00:00`)),
        scheduledLabel: shiftLabelForSchedule(row),
        reason: scheduleIssueReason,
        note: scheduleIssueNote.trim(),
        status: "open",
        source: "clock_in_schedule",
        createdAt: serverTimestamp(),
        createdByUid: auth?.currentUser?.uid || "",
      };
      const issueRef = await addDoc(collection(db, "orgs", orgId, "neoScheduleIssues"), createdIssue);
      setStaffScheduleIssues((current) => [
        { ...createdIssue, id: issueRef.id, createdAt: new Date() },
        ...current,
      ]);
      setScheduleIssueSentDate(staffScheduleSelectedDate);
      setScheduleIssueOpen(false);
      setScheduleIssueReason("");
      setScheduleIssueNote("");
      showToast("Schedule issue sent to management ✓", "success");
    } catch (error) {
      console.error("[kiosk] schedule issue submit failed:", error);
      showToast("Could not send the schedule issue. Please try again.", "error");
    } finally {
      setScheduleIssueSending(false);
    }
  };

  const goToWorkspace = async () => {
    if (!selectedStaff || !pin) {
      showToast(
        isSecurityMode
          ? "Select your name and enter your security PIN to open Workspace."
          : "Enter your name and PIN to open Workspace.",
        "error"
      );
      return;
    }

    const user = getSelectedUser();
    if (!user || String(user.pin) !== String(pin)) {
      showToast(!user ? "Please select a valid staff member." : "Incorrect PIN.", "error");
      return;
    }

    const { liveOpenShift } = await resolveLiveOpenShift(
      orgId,
      storeId,
      selectedStaff
    );

    if (!liveOpenShift) {
      showToast("You must be clocked in to open Workspace.", "error");
      return;
    }

    writeStaffSession(user, liveOpenShift.id || null);
    navigate("/staff-dashboard", {
      state: {
        staffSession: null,
        source: "clockin_workspace",
        nonce: Date.now(),
      },
    });
  };

  const goToDashboard = async () => {
    if (!selectedStaff || !pin) {
      showToast(
        isSecurityMode
          ? "Select your name and enter your security PIN to open My Activity."
          : "Enter your name and PIN to open Dashboard.",
        "error"
      );
      return;
    }

    const user = getSelectedUser();
    if (!user || String(user.pin) !== String(pin)) {
      showToast(!user ? "Please select a valid staff member." : "Incorrect PIN.", "error");
      return;
    }

    const { liveOpenShift } = await resolveLiveOpenShift(
      orgId,
      storeId,
      selectedStaff
    );

    if (!liveOpenShift) {
      showToast("You must be clocked in to open Dashboard.", "error");
      return;
    }

    writeStaffSession(user, liveOpenShift.id || null);
    navigate("/staff-portal", {
      state: {
        staffSession: null,
        source: "clockin_portal",
        nonce: Date.now(),
      },
    });
  };

  useEffect(() => {
    return () => {
      if (securityPhotoPreview) URL.revokeObjectURL(securityPhotoPreview);
    };
  }, [securityPhotoPreview]);

  const handleLivePhotoCaptured = (file) => {
    // LiveClockInCamera supplies only a freshly captured/compressed JPEG.
    if (!canCaptureSecurityPhoto || !file || file.type !== "image/jpeg" || file.size >= 5 * 1024 * 1024) {
      setLiveCameraOpen(false);
      showToast("Photo verification expired. Re-enter your PIN and try again.", "error");
      return;
    }
    setSecurityPhotoFile(file);
    setSecurityPhotoPreview(URL.createObjectURL(file));
    setLiveCameraOpen(false);
  };

  const timeText = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const dateText = now.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const isClockingIn = clockActionBusy === "in";
  const isClockingOut = clockActionBusy === "out";
  const isClockActionBusy = Boolean(clockActionBusy);

  return (
    <div className="page" style={styles.page}>
      {liveCameraOpen && (isClockedIn ? photoRequiredForClockOut : photoRequiredForClockIn) && canCaptureSecurityPhoto && !isClockActionBusy && (
        <LiveClockInCamera
          staffName={selectedStaff}
          onClose={() => setLiveCameraOpen(false)}
          onCapture={handleLivePhotoCaptured}
        />
      )}
      <style>{`
        @keyframes neoClockButtonSpin { to { transform: rotate(360deg); } }
        .neo-clock-form-grid { grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 1fr) minmax(170px, .9fr); align-items: start; }
        .neo-clock-photo-block { grid-column: auto; }
        .neo-shift-preview-grid { grid-template-columns: 1fr 1fr; }
        @media (max-width: 820px) {
          .neo-clock-form-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 560px) {
          .neo-clock-hero { padding: 18px 16px 16px !important; }
          .neo-clock-hero-row { grid-template-columns: 1fr !important; gap: 14px !important; }
          .neo-clock-hero-right { min-width: 0 !important; align-items: flex-start !important; gap: 6px !important; }
          .neo-clock-live-time { font-size: 2rem !important; }
          .neo-clock-live-date { text-align: left !important; font-size: 13px !important; }
          .neo-clock-main-card { padding: 14px !important; }
          .neo-clock-quick-grid { gap: 10px !important; }
          .neo-clock-quick-grid > button { min-width: 0 !important; padding-left: 10px !important; padding-right: 10px !important; font-size: 14px !important; }
          .neo-clock-form-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
          .neo-clock-form-grid > * { min-width: 0 !important; }
          .neo-clock-form-grid input, .neo-clock-form-grid select, .neo-clock-form-grid button { box-sizing: border-box !important; min-width: 0 !important; width: 100% !important; }
          .neo-shift-preview-grid { grid-template-columns: 1fr; }
          .neo-clock-schedule-drawer { width: 100vw !important; max-width: 100vw !important; padding: 16px !important; box-sizing: border-box !important; }
        }
        @media (max-width: 390px) {
          .neo-clock-quick-grid { grid-template-columns: 1fr 1fr !important; }
          .neo-clock-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={styles.surface}>
        <div className="neo-clock-hero" style={styles.hero}>
          <div style={styles.heroGlow} />

          <div className="neo-clock-hero-row" style={styles.heroTopRow}>
            <div style={styles.heroLeft}>
              <div style={styles.welcomeEyebrow}>Welcome to <span style={styles.welcomeNeo}>Neo</span></div>
              <h1 style={styles.title}>
                {storeName}
              </h1>
              <div style={styles.subtitle}>
                {isSecurityMode
                  ? "Security attendance, workspace access, patrol entry, and incident capture"
                  : "Staff clock in, workspace access, and personal dashboard entry"}
              </div>
            </div>

            <div className="neo-clock-hero-right" style={styles.heroRight}>
              <div style={online ? styles.netPillOnline : styles.netPillOffline}>
                {online ? "● Online" : "● Offline"}
              </div>
              <div className="neo-clock-live-time" style={styles.liveClock}>{timeText}</div>
              <div className="neo-clock-live-date" style={styles.liveDate}>{dateText}</div>
            </div>
          </div>
        </div>

        <div className="neo-clock-main-card" style={styles.mainCard}>
          <div style={styles.quickActionsLabel}>Quick Actions</div>

          <div className="neo-clock-quick-grid" style={styles.quickActionsGrid}>
            <button
              onClick={() => setShowClockPanel((v) => !v)}
              style={isClockedIn ? styles.secondaryBtnDanger : styles.secondaryBtn}
            >
              {showClockPanel ? "Close Clock Panel" : isClockedIn ? "Clock Out →" : "Clock In →"}
            </button>

            <button
              onClick={goToWorkspace}
              style={isClockedIn ? styles.secondaryBtn : styles.secondaryBtnAlt}
            >
              Workspace →
            </button>

            {isSecurityMode ? (
              <>
                <button onClick={() => navigate("/security/patrols")} style={styles.secondaryBtnAlt}>
                  Patrols →
                </button>
                <button onClick={() => navigate("/security/incidents")} style={styles.secondaryBtnAlt}>
                  Incidents →
                </button>
                <button onClick={goToDashboard} style={styles.secondaryBtnAlt}>
                  My Activity →
                </button>
              </>
            ) : (
              <button onClick={goToDashboard} style={styles.dashboardQuickBtn}>
                Dashboard →
              </button>
            )}

            <button
              onClick={() => navigate("/manager-login")}
              style={styles.managerBtn}
            >
              Manager Login
            </button>
          </div>

          <div style={styles.managerActionsGrid}>
            <button
              onClick={handleRefresh}
              style={{
                ...styles.refreshBtn,
                ...(updateAvailable
                  ? {
                      border: "1px solid rgba(245,158,11,.72)",
                      boxShadow: "0 0 0 3px rgba(245,158,11,.10)",
                    }
                  : {}),
              }}
            >
              {refreshState === "refreshing"
                ? "🔄 Refreshing…"
                : updateAvailable
                ? `✨ New update · Refresh${updateAvailable.latestBuild ? ` · Build ${updateAvailable.latestBuild}` : ""}`
                : refreshState === "done"
                ? "🔄 Refresh ✓"
                : `🔄 Refresh · Build ${NEO_BUILD_ID}`}
            </button>
          </div>

          {showClockPanel ? (
            <div style={styles.clockPanel}>
              <div style={styles.clockPanelTitle}>
                {isClockedIn ? "Clock out" : "Clock in"}
              </div>
              <div className="neo-clock-form-grid" style={styles.formGrid}>
                <div style={styles.fieldBlock}>
                  <label style={styles.label}>Select your name</label>
                  <select
                    value={selectedStaff}
                    onChange={(e) => handleStaffSelect(e.target.value)}
                    style={styles.selectInput}
                  >
                    <option value="" style={styles.selectOption}>
                      {staffList.length ? "Select your name" : "No staff found"}
                    </option>
                    {staffList.map((u) => (
                      <option key={u.id} value={u.name} style={styles.selectOption}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>

                {!isClockedIn ? (
                  <div style={styles.fieldBlock}>
                    <label style={styles.label}>Select role</label>
                    <select
                      value={selectedRole}
                      onChange={(e) => {
                        if (e.target.value !== selectedRole) {
                          setSecurityPhotoFile(null);
                          setSecurityPhotoPreview("");
                          setLiveCameraOpen(false);
                        }
                        setSelectedRole(e.target.value);
                      }}
                      style={styles.selectInput}
                    >
                      <option value="" style={styles.selectOption}>Select role</option>
                      {roles.map((r) => (
                        <option key={r} value={r} style={styles.selectOption}>{r}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={styles.fieldBlock}>
                    <label style={styles.label}>Role</label>
                    <div style={styles.readOnlyField}>{selectedRole || openShift?.role || "—"}</div>
                  </div>
                )}

                <div style={styles.fieldBlock}>
                  <label style={styles.label}>
                    {isSecurityMode ? "Enter security PIN" : "Enter PIN"}
                  </label>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={pin}
                    placeholder={isSecurityMode ? "Enter security PIN" : "Enter PIN"}
                    onChange={onPinChange}
                    style={styles.input}
                    autoComplete="one-time-code"
                  />
                  <div style={styles.hint}>
                    {isSecurityMode
                      ? "Personal PIN required."
                      : "PIN hides automatically."}
                    {!photoPolicyReady ? " · Loading attendance verification settings…" : ""}
                  </div>
                </div>

                <div style={styles.clockActionBlock}>
                  <label style={styles.label}>{isClockedIn ? "End shift" : "Start shift"}</label>
                  {isClockedIn ? (
                    <>
                    <button
                      onClick={handleClockOutClick}
                      disabled={isClockActionBusy || !photoPolicyReady || (photoRequiredForClockOut && (!canCaptureSecurityPhoto || !securityPhotoFile || !online))}
                      aria-busy={isClockActionBusy}
                      style={{
                        ...styles.primaryActionBtn,
                        ...styles.clockOutBtn,
                        ...(isClockActionBusy ? styles.primaryActionBtnDisabled : {}),
                      }}
                    >
                      {isClockingOut ? (
                        <span style={styles.buttonLoadingContent}>
                          <span style={styles.buttonSpinner} /> Clocking out…
                        </span>
                      ) : (
                        "🔴 Clock Out"
                      )}
                    </button>
                    {photoRequiredForClockOut ? (
                      <div className="neo-clock-photo-block" style={styles.securityPhotoBlock}>
                        <div style={styles.hint} aria-live="polite">
                          {!canCaptureSecurityPhoto ? "1. Enter the correct PIN to enable the camera."
                            : !securityPhotoFile ? "2. Take a photo to enable Clock Out."
                            : !online ? "3. Photo captured — reconnect to finish Clock Out."
                            : "3. Photo captured — Clock Out is ready."}
                        </div>
                        <button type="button"
                          onClick={() => { if (canCaptureSecurityPhoto && !isClockActionBusy) setLiveCameraOpen(true); }}
                          disabled={!canCaptureSecurityPhoto || isClockActionBusy}
                          style={!canCaptureSecurityPhoto ? styles.photoCaptureDisabledBtn :
                            securityPhotoPreview ? styles.photoCaptureDoneBtn : styles.photoCaptureReadyBtn}
                        >
                          {!canCaptureSecurityPhoto ? "Enter correct PIN first" : securityPhotoPreview ? "Retake photo" : "📷 Open camera"}
                        </button>
                        {securityPhotoPreview ? (
                          <div style={styles.photoPreviewWrap}>
                            <img src={securityPhotoPreview} alt="Clock-out preview" style={styles.photoPreview} />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleClockIn}
                        disabled={isClockActionBusy || !photoPolicyReady || (photoRequiredForClockIn && (!canCaptureSecurityPhoto || !securityPhotoFile))}
                        aria-busy={isClockActionBusy}
                        style={{
                          ...styles.primaryActionBtn,
                          ...styles.clockInBtn,
                          ...((isClockActionBusy || !photoPolicyReady || (photoRequiredForClockIn && (!canCaptureSecurityPhoto || !securityPhotoFile)))
                            ? styles.primaryActionBtnDisabled : {}),
                        }}
                      >
                        {isClockingIn ? (
                          <span style={styles.buttonLoadingContent}>
                            <span style={styles.buttonSpinner} /> Clocking in…
                          </span>
                        ) : (
                          "✅ Clock In"
                        )}
                      </button>
                      {photoRequiredForClockIn ? (
                        <div className="neo-clock-photo-block" style={styles.securityPhotoBlock}>
                          <div style={styles.hint} aria-live="polite">
                            {!canCaptureSecurityPhoto
                              ? "1. Select staff and role, then enter the correct PIN."
                              : !securityPhotoFile
                              ? "2. Take a photo to enable Clock In."
                              : "3. Photo captured — Clock In is ready."}
                          </div>
                          <button
                            type="button"
                            onClick={() => { if (canCaptureSecurityPhoto && !isClockActionBusy) setLiveCameraOpen(true); }}
                            disabled={!canCaptureSecurityPhoto || isClockActionBusy}
                            style={
                              !canCaptureSecurityPhoto
                                ? styles.photoCaptureDisabledBtn
                                : securityPhotoPreview
                                ? styles.photoCaptureDoneBtn
                                : styles.photoCaptureReadyBtn
                            }
                          >
                            {!canCaptureSecurityPhoto
                              ? "Enter correct PIN first"
                              : securityPhotoPreview
                              ? "Retake photo"
                              : "📷 Open camera"}
                          </button>
                          {securityPhotoPreview ? (
                            <div style={styles.photoPreviewWrap}>
                              <img src={securityPhotoPreview} alt="Clock-in preview" style={styles.photoPreview} />
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              <div style={styles.shiftPreviewCard}>
                <div style={styles.shiftPreviewHeader}>
                  <div>
                    <div style={styles.shiftPreviewEyebrow}>Selected staff schedule</div>
                    <div style={styles.shiftPreviewTitle}>Today & tomorrow</div>
                  </div>
                  <button type="button" onClick={openStaffSchedule} style={styles.viewPortalBtn}>
                    Schedule →
                  </button>
                </div>

                <div className="neo-shift-preview-grid" style={styles.shiftPreviewGrid}>
                  <div style={styles.shiftPreviewItem}>
                    <div style={styles.shiftDay}>Today</div>
                    <div style={styles.shiftValue}>
                      {!selectedStaff
                        ? "Select a staff member"
                        : selectedScheduleLoading
                        ? "Loading…"
                        : shiftLabelForSchedule(selectedSchedule.today)}
                    </div>
                    <div style={styles.shiftMeta}>
                      {selectedStaff ? (selectedSchedule.today?.roleName || selectedSchedule.today?.role || selectedRole || "") : ""}
                    </div>
                  </div>

                  <div style={styles.shiftPreviewItem}>
                    <div style={styles.shiftDay}>Tomorrow</div>
                    <div style={styles.shiftValue}>
                      {!selectedStaff
                        ? "Select a staff member"
                        : selectedScheduleLoading
                        ? "Loading…"
                        : shiftLabelForSchedule(selectedSchedule.tomorrow)}
                    </div>
                    <div style={styles.shiftMeta}>
                      {selectedStaff ? (selectedSchedule.tomorrow?.roleName || selectedSchedule.tomorrow?.role || selectedRole || "") : ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {!!statusLine && <div style={styles.statusLine}>{statusLine}</div>}
        </div>

        <div style={styles.msgCard}>
          <div style={styles.msgHeader}>
            <div>
              <div style={styles.msgTitle}>Daily Message</div>
              <div style={styles.msgSub}>For everyone at {storeName} {isSecurityMode ? "Site" : "store"}</div>
            </div>
          </div>
          <div style={styles.msgBody}>
            {dailyMessage ? (
              <div style={styles.messageText}>{dailyMessage}</div>
            ) : (
              <em style={styles.emptyMessage}>No message set for today.</em>
            )}
          </div>
        </div>

        <div style={styles.matrixFooter} aria-label="Powered by MatrixOS">
          <span style={styles.matrixFooterPowered}>Powered by MatrixOS</span>
          <span style={styles.matrixFooterDot}>•</span>
          <span style={styles.matrixFooterTagline}>Operate with Clarity.</span>
        </div>
      </div>


      {staffScheduleOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => { setStaffScheduleOpen(false); setStaffScheduleView("month"); }}
          style={styles.scheduleOverlay}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={styles.scheduleDrawer}
          >
            <div style={styles.scheduleDrawerHeader}>
              <div>
                <div style={styles.shiftPreviewEyebrow}>MY PUBLISHED ROSTER</div>
                <div style={styles.scheduleDrawerTitle}>{staffScheduleStaffName || selectedStaff}'s Schedule</div>
                <div style={styles.scheduleDrawerSub}>
                  Read-only full-month view from Neo Schedule Intelligence.
                </div>
              </div>
              {staffScheduleView === "month" ? (
                <button
                  type="button"
                  aria-label="Close schedule"
                  onClick={() => { setStaffScheduleOpen(false); setStaffScheduleView("month"); }}
                  style={styles.scheduleCloseBtn}
                >
                  ×
                </button>
              ) : null}
            </div>

            {staffScheduleLoading ? (
              <div style={styles.scheduleEmpty}>Loading your schedule…</div>
            ) : (
              <>
                {staffScheduleView === "month" ? (<>
                <div style={styles.scheduleMonthHeading}>
                  {new Date().toLocaleDateString([], { month: "long", year: "numeric" })}
                </div>

                {(() => {
                  const monthIntel = buildKioskMonthIntelligence({
                    entries: staffScheduleRows,
                    shifts: staffScheduleShifts,
                    now,
                    activeShiftId: openShiftId || "",
                  });
                  const variancePositive = monthIntel.varianceHours >= 0;
                  return (
                    <div style={styles.scheduleMonthIntelCard}>
                      <div style={styles.scheduleMonthIntelHeader}>
                        <div>
                          <div style={styles.scheduleMonthIntelEyebrow}>MONTH INTELLIGENCE</div>
                          <div style={styles.scheduleMonthIntelTitle}>Where you stand this month</div>
                        </div>
                        <div style={{
                          ...styles.scheduleMonthIntelVariance,
                          color: Math.abs(monthIntel.varianceHours) < 1 / 120
                            ? (isLight ? "#475569" : "#cbd5e1")
                            : variancePositive ? "#34d399" : "#fbbf24",
                        }}>
                          {kioskSignedDurationLabel(monthIntel.varianceHours)}
                        </div>
                      </div>
                      <div style={styles.scheduleMonthIntelGrid}>
                        <div style={styles.scheduleMonthIntelStat}>
                          <span>Scheduled to date</span>
                          <strong>{kioskDurationLabel(monthIntel.scheduledToDate)}</strong>
                        </div>
                        <div style={styles.scheduleMonthIntelStat}>
                          <span>Credited to date</span>
                          <strong>{kioskDurationLabel(monthIntel.creditedToDate)}</strong>
                        </div>
                        <div style={styles.scheduleMonthIntelStat}>
                          <span>Published month</span>
                          <strong>{kioskDurationLabel(monthIntel.publishedMonthHours)}</strong>
                        </div>
                        <div style={styles.scheduleMonthIntelStat}>
                          <span>Projected month end</span>
                          <strong>{kioskDurationLabel(monthIntel.projectedMonthEndHours)}</strong>
                        </div>
                      </div>
                      <div style={styles.scheduleMonthIntelNote}>
                        Based on your published roster and approved credited time through right now.
                      </div>
                    </div>
                  );
                })()}

                {[
                  getMonthScheduleDates().slice(0, 16),
                  getMonthScheduleDates().slice(16),
                ].map((group, groupIndex) => (
                  <div key={`month-group-${groupIndex}`} style={styles.scheduleMonthSection}>
                    <div style={styles.scheduleMonthSectionLabel}>
                      {groupIndex === 0 ? "Days 1–16" : `Days 17–${getMonthScheduleDates().length}`}
                    </div>
                    <div style={styles.scheduleMonthGrid}>
                      {group.map((date) => {
                        const parsed = new Date(`${date}T12:00:00`);
                        const selected = date === staffScheduleSelectedDate;
                        const today = date === neoDateKey(new Date());
                        const row =
                          staffScheduleRows.find(
                            (entry) => String(entry.date || "") === String(date)
                          ) || null;
                        const shiftText = row ? shiftLabelForSchedule(row) : "—";
                        const isOff = String(shiftText).toUpperCase() === "OFF";
                        const isLeave = Boolean(row?.isAttendance === true || row?.attendanceTypeId);
                        const dateIssues = staffScheduleIssues.filter((issue) => String(issue.date || "") === String(date));
                        const openIssueCount = dateIssues.filter((issue) => !["resolved", "closed"].includes(String(issue.status || "").toLowerCase())).length;

                        return (
                          <button
                            key={date}
                            type="button"
                            onClick={() => {
                              setStaffScheduleSelectedDate(date);
                              setScheduleIssueOpen(false);
                              setScheduleIssueReason("");
                              setScheduleIssueNote("");
                              setStaffScheduleView("day");
                            }}
                            style={{
                              ...styles.scheduleMonthDayBtn,
                              ...(selected ? styles.scheduleDateBtnSelected : {}),
                              ...(today ? styles.scheduleMonthDayToday : {}),
                              ...(isOff ? styles.scheduleMonthDayOff : {}),
                              ...(isLeave ? styles.scheduleMonthDayLeave : {}),
                            }}
                          >
                            <div style={styles.scheduleDateDow}>
                              {parsed.toLocaleDateString([], { weekday: "short" })}
                            </div>
                            <div style={styles.scheduleMonthDayTop}>
                              <span style={styles.scheduleDateNum}>{parsed.getDate()}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {dateIssues.length ? (
                                  <span
                                    title={`${dateIssues.length} submitted schedule report${dateIssues.length === 1 ? "" : "s"}`}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: 20,
                                      height: 20,
                                      padding: "0 6px",
                                      borderRadius: 999,
                                      border: `1px solid ${openIssueCount ? "rgba(251,191,36,.48)" : "rgba(52,211,153,.38)"}`,
                                      background: openIssueCount ? "rgba(251,191,36,.14)" : "rgba(52,211,153,.12)",
                                      color: openIssueCount ? "#fbbf24" : "#34d399",
                                      fontSize: 9,
                                      fontWeight: 950,
                                    }}
                                  >
                                    {dateIssues.length}
                                  </span>
                                ) : null}
                                {today ? <span style={styles.scheduleToday}>TODAY</span> : null}
                              </div>
                            </div>
                            <div style={styles.scheduleMonthShiftText}>{shiftText}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                </>) : null}

                {staffScheduleView === "day" ? (() => {
                  const row =
                    staffScheduleRows.find(
                      (entry) => String(entry.date || "") === String(staffScheduleSelectedDate || "")
                    ) || null;
                  const shiftText = shiftLabelForSchedule(row);
                  const intelligence = buildKioskDayIntelligence({
                    entry: row,
                    shifts: staffScheduleShifts,
                    dateKey: staffScheduleSelectedDate,
                    now,
                  });

                  return (
                    <div style={styles.scheduleDayPanel}>
                      <div style={styles.scheduleDayPanelHeader}>
                        <button
                          type="button"
                          onClick={() => {
                            setScheduleIssueOpen(false);
                            setScheduleIssueReason("");
                            setScheduleIssueNote("");
                            setStaffScheduleView("month");
                          }}
                          style={styles.scheduleBackBtn}
                        >
                          ← Back to month
                        </button>
                        <div style={styles.shiftPreviewEyebrow}>DAY INTELLIGENCE</div>
                      </div>

                      <div style={styles.scheduleSelectedCard}>
                        <div style={styles.shiftDay}>Selected day</div>
                        <div style={styles.scheduleSelectedTitle}>
                          {staffScheduleSelectedDate
                            ? new Date(`${staffScheduleSelectedDate}T12:00:00`).toLocaleDateString([], {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                              })
                            : "—"}
                        </div>
                      </div>

                      <div style={styles.scheduleStaffCard}>
                        <div>
                          <div style={styles.scheduleStaffName}>{staffScheduleStaffName || selectedStaff}</div>
                          <div style={styles.scheduleStaffRole}>
                            {row?.roleName || row?.role || staffScheduleRole || selectedRole || "Role not set"}
                          </div>
                        </div>
                        <div
                          style={{
                            ...styles.scheduleShiftValue,
                            ...(String(shiftText).toUpperCase() === "OFF"
                              ? styles.scheduleShiftOff
                              : {}),
                          }}
                        >
                          {row ? shiftText : "Not scheduled"}
                        </div>
                      </div>

                      <div style={styles.scheduleIntelligenceCard}>
                        <div style={styles.scheduleIntelligenceHeader}>
                          <div style={styles.scheduleIntelligenceTitle}>What happened</div>
                          <div style={{ ...styles.scheduleStatusPill, color: intelligence.tone, borderColor: `${intelligence.tone}66`, background: `${intelligence.tone}14` }}>
                            {intelligence.status}
                          </div>
                        </div>

                        <div style={styles.scheduleIntelligenceGrid}>
                          <div style={styles.scheduleIntelBlock}>
                            <div style={styles.scheduleIntelLabel}>SCHEDULED</div>
                            <div style={styles.scheduleIntelValue}>{row ? shiftText : "Not scheduled"}</div>
                            <div style={styles.scheduleIntelSub}>{intelligence.scheduledPaid > 0 ? `${kioskDurationLabel(intelligence.scheduledPaid)} paid time` : "No paid scheduled time"}</div>
                          </div>
                          <div style={styles.scheduleIntelBlock}>
                            <div style={styles.scheduleIntelLabel}>ACTUAL</div>
                            <div style={styles.scheduleIntelValue}>{intelligence.actual}</div>
                            <div style={styles.scheduleIntelSub}>{intelligence.creditLabel}</div>
                          </div>
                        </div>

                        <div style={styles.scheduleDecisionRow}>
                          <span>Manager decision / state</span>
                          <strong>{intelligence.decision}</strong>
                        </div>
                      </div>

                      {(() => {
                        const todayKey = neoDateKey(now);
                        const futureShift = Boolean(
                          row &&
                          staffScheduleSelectedDate > todayKey &&
                          String(shiftText || "").toUpperCase() !== "OFF" &&
                          !row?.isAttendance &&
                          !row?.attendanceTypeId
                        );
                        if (!futureShift) return null;
                        const alreadySent = scheduleIssueSentDate === staffScheduleSelectedDate;
                        return (
                          <div style={styles.scheduleIssueCard}>
                            <div style={styles.scheduleIssueHeader}>
                              <div>
                                <div style={styles.scheduleIntelLabel}>SCHEDULE SUPPORT</div>
                                <div style={styles.scheduleIssueTitle}>Something wrong with this shift?</div>
                              </div>
                              {alreadySent ? <div style={styles.scheduleIssueSent}>Sent ✓</div> : null}
                            </div>

                            {!scheduleIssueOpen ? (
                              <>
                                <div style={styles.scheduleIssueCopy}>
                                  Flag a future roster problem for management without changing the published schedule.
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setScheduleIssueOpen(true)}
                                  style={styles.scheduleIssueBtn}
                                >
                                  Report schedule issue →
                                </button>
                              </>
                            ) : (
                              <div style={styles.scheduleIssueForm}>
                                <label style={styles.scheduleIssueLabel}>
                                  What is the issue?
                                  <select
                                    value={scheduleIssueReason}
                                    onChange={(event) => setScheduleIssueReason(event.target.value)}
                                    style={styles.scheduleIssueSelect}
                                  >
                                    <option value="">Select a reason…</option>
                                    <option value="Cannot work this shift">Cannot work this shift</option>
                                    <option value="Shift time looks incorrect">Shift time looks incorrect</option>
                                    <option value="Scheduled on approved leave">Scheduled on approved leave</option>
                                    <option value="Other">Other</option>
                                  </select>
                                </label>
                                <label style={styles.scheduleIssueLabel}>
                                  Note (optional)
                                  <textarea
                                    value={scheduleIssueNote}
                                    onChange={(event) => setScheduleIssueNote(event.target.value)}
                                    placeholder="Add a short note for your manager"
                                    rows={3}
                                    style={styles.scheduleIssueTextarea}
                                  />
                                </label>
                                <div style={styles.scheduleIssueActions}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setScheduleIssueOpen(false);
                                      setScheduleIssueReason("");
                                      setScheduleIssueNote("");
                                    }}
                                    style={styles.scheduleIssueCancelBtn}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    disabled={!scheduleIssueReason || scheduleIssueSending}
                                    onClick={submitScheduleIssue}
                                    style={{
                                      ...styles.scheduleIssueSubmitBtn,
                                      opacity: !scheduleIssueReason || scheduleIssueSending ? .55 : 1,
                                    }}
                                  >
                                    {scheduleIssueSending ? "Sending…" : "Send to management"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {(() => {
                        const dayIssues = staffScheduleIssues
                          .filter((issue) => String(issue.date || "") === String(staffScheduleSelectedDate || ""))
                          .sort((a, b) => (tsToDate(b.createdAt)?.getTime?.() || 0) - (tsToDate(a.createdAt)?.getTime?.() || 0));
                        if (!dayIssues.length) return null;
                        const statusMeta = (status) => {
                          const value = String(status || "open").toLowerCase();
                          if (value === "resolved" || value === "closed") return { label: "Resolved", color: "#34d399" };
                          if (value === "acknowledged" || value === "reviewing") return { label: "Under review", color: "#60a5fa" };
                          return { label: "Submitted", color: "#fbbf24" };
                        };
                        return (
                          <div style={{ ...styles.scheduleIssueCard, marginTop: 14 }}>
                            <div style={styles.scheduleIssueHeader}>
                              <div>
                                <div style={styles.scheduleIntelLabel}>SUBMITTED REPORTS</div>
                                <div style={styles.scheduleIssueTitle}>Schedule issue history</div>
                              </div>
                              <div style={styles.scheduleIssueSent}>{dayIssues.length}</div>
                            </div>
                            <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                              {dayIssues.map((issue) => {
                                const meta = statusMeta(issue.status);
                                const createdAt = tsToDate(issue.createdAt);
                                return (
                                  <div key={issue.id} style={{ border: "1px solid rgba(148,163,184,.16)", borderRadius: 14, padding: 12, background: "rgba(15,23,42,.22)" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                                      <div>
                                        <div style={{ fontWeight: 900 }}>{issue.reason || "Schedule issue"}</div>
                                        {issue.note ? <div style={{ marginTop: 4, fontSize: 12, opacity: .82 }}>{issue.note}</div> : null}
                                      </div>
                                      <span style={{ color: meta.color, border: `1px solid ${meta.color}55`, background: `${meta.color}14`, borderRadius: 999, padding: "5px 8px", fontSize: 10, fontWeight: 950, whiteSpace: "nowrap" }}>{meta.label}</span>
                                    </div>
                                    <div style={{ marginTop: 9, fontSize: 11, opacity: .68 }}>
                                      {createdAt ? `Submitted ${createdAt.toLocaleDateString([], { day: "numeric", month: "short" })} · ${createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Submitted to management"}
                                    </div>
                                    {issue.managerResponse ? (
                                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(148,163,184,.14)", fontSize: 12 }}>
                                        <strong>Manager response:</strong> {issue.managerResponse}
                                      </div>
                                    ) : null}
                                    {issue.resolvedByName ? <div style={{ marginTop: 5, fontSize: 11, opacity: .68 }}>Updated by {issue.resolvedByName}</div> : null}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })() : null}
              </>
            )}
          </div>
        </div>
      )}

      {confirmClockOut && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Confirm Clock Out</div>
            <div style={styles.modalMessage}>{`Clock out ${selectedStaff}?`}</div>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setConfirmClockOut(false)}
                disabled={isClockActionBusy}
                style={{
                  ...styles.modalCancelBtn,
                  ...(isClockActionBusy ? styles.modalBtnDisabled : {}),
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClockOut}
                disabled={isClockActionBusy}
                aria-busy={isClockActionBusy}
                style={{
                  ...styles.modalConfirmBtn,
                  ...(isClockActionBusy ? styles.modalBtnDisabled : {}),
                }}
              >
                {isClockingOut ? (
                  <span style={styles.buttonLoadingContent}>
                    <span style={styles.buttonSpinner} /> Clocking out…
                  </span>
                ) : (
                  "Yes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <InlineToast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "info" })}
      />
    </div>
  );
}

/* ---------------- themed styles ---------------- */
const getStyles = (themeMode = "dark") => {
  const isLight = themeMode === "light";

  const c = {
    pageText: isLight ? "#14253a" : "#e6eef8",
    shellBg: isLight
      ? "radial-gradient(circle at 50% 0%, rgba(42,127,255,0.10), transparent 20%), radial-gradient(circle at 0% 50%, rgba(42,127,255,0.05), transparent 26%), linear-gradient(180deg, #f3f7fc, #eef3f9)"
      : "radial-gradient(circle at 50% 0%, rgba(77,162,255,0.18), transparent 20%), radial-gradient(circle at 0% 50%, rgba(0,130,255,0.08), transparent 26%), linear-gradient(180deg, rgba(3,10,22,0.99), rgba(2,8,18,1))",
    shellBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(98,165,255,0.14)",
    shellShadow: isLight
      ? "0 20px 55px rgba(23,43,77,0.08), inset 0 1px 0 rgba(255,255,255,0.9)"
      : "0 28px 90px rgba(0,0,0,0.52), inset 0 1px 0 rgba(255,255,255,0.04)",

    heroBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f5f9ff)"
      : "linear-gradient(180deg, rgba(9,24,46,0.98), rgba(6,17,34,0.95))",
    heroBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(97,171,255,0.16)",
    heroShadow: isLight
      ? "0 10px 30px rgba(28,52,84,0.08), inset 0 1px 0 rgba(255,255,255,0.95)"
      : "0 16px 34px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.05)",

    heroBadgeText: isLight ? "#2a7fff" : "#7db8ff",
    heroBadgeBg: isLight ? "rgba(42,127,255,0.08)" : "rgba(57,129,255,0.10)",
    heroBadgeBorder: isLight ? "rgba(42,127,255,0.14)" : "rgba(125,184,255,0.18)",

    titleMain: isLight ? "#10243b" : "#f7fbff",
    titleStore: isLight ? "#2a7fff" : "#53a7ff",
    subtitle: isLight ? "#60758f" : "#aebfd4",
    welcome: isLight ? "#617790" : "#b7c7db",

    onlineBg: isLight ? "rgba(32,183,109,0.10)" : "rgba(61,220,151,0.12)",
    onlineText: isLight ? "#14995a" : "#6ef0b1",
    onlineBorder: isLight ? "rgba(32,183,109,0.14)" : "rgba(61,220,151,0.22)",

    offlineBg: isLight ? "rgba(255,107,107,0.10)" : "rgba(255,107,107,0.12)",
    offlineText: isLight ? "#d64545" : "#ff9a9a",
    offlineBorder: isLight ? "rgba(255,107,107,0.14)" : "rgba(255,107,107,0.20)",

    timeText: isLight ? "#16324e" : "#f5f9ff",
    dateText: isLight ? "#69809b" : "#a8b9cd",

    cardBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f8fbff)"
      : "linear-gradient(180deg, rgba(15,32,58,0.98), rgba(10,24,43,0.98))",
    cardBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(123,186,255,0.22)",
    cardShadow: isLight
      ? "0 10px 28px rgba(22,47,79,0.08), inset 0 1px 0 rgba(255,255,255,0.95)"
      : "0 18px 34px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.05)",

    fieldLabel: isLight ? "#17314b" : "#e7eef8",
    hint: isLight ? "#7589a1" : "#8ea3bc",
    inputBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f7fbff)"
      : "linear-gradient(180deg, rgba(22,42,70,0.98), rgba(14,30,52,0.98))",
    inputBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(123,186,255,0.22)",
    inputText: isLight ? "#17314b" : "#eef5ff",
    inputShadow: isLight
      ? "0 1px 2px rgba(22,47,79,0.04)"
      : "inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 18px rgba(0,0,0,0.16), 0 0 0 1px rgba(88,154,255,0.04)",

    clockInBg: isLight
      ? "linear-gradient(180deg,#20b76d,#14995a)"
      : "linear-gradient(180deg,#29c977,#159a59)",
    clockInShadow: isLight
      ? "0 12px 24px rgba(20,153,90,0.18)"
      : "0 14px 28px rgba(31,157,85,0.24)",
    clockOutBg: isLight
      ? "linear-gradient(180deg,#ef4444,#dc2626)"
      : "linear-gradient(180deg,#ef4444,#b91c1c)",
    clockOutShadow: isLight
      ? "0 12px 24px rgba(220,38,38,0.16)"
      : "0 14px 28px rgba(185,28,28,0.24)",

    secondaryBtnBg: isLight
      ? "linear-gradient(180deg,#2a7fff,#1f63d9)"
      : "linear-gradient(180deg,#2d8cff,#1f6fe0)",
    secondaryBtnShadow: isLight
      ? "0 10px 20px rgba(31,99,217,0.18)"
      : "0 12px 24px rgba(47,128,237,0.24)",

    secondaryBtnAltBg: isLight
      ? "linear-gradient(180deg,#ffffff,#eef5ff)"
      : "linear-gradient(180deg, rgba(20,39,65,0.96), rgba(11,24,44,0.96))",
    secondaryBtnAltText: isLight ? "#204a75" : "#dce9fb",
    secondaryBtnAltBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(111,168,255,0.18)",
    secondaryBtnAltShadow: isLight
      ? "0 8px 16px rgba(22,47,79,0.06)"
      : "0 10px 20px rgba(0,0,0,0.18)",

    scheduleBtnBg: isLight
      ? "linear-gradient(180deg,#8b5cf6,#7c3aed)"
      : "linear-gradient(180deg,#8b5cf6,#6d28d9)",
    scheduleBtnText: "#ffffff",
    scheduleBtnBorder: isLight ? "rgba(109,40,217,0.28)" : "rgba(167,139,250,0.34)",
    scheduleBtnShadow: isLight
      ? "0 8px 18px rgba(124,58,237,0.20)"
      : "0 10px 22px rgba(109,40,217,0.26)",

    managerBtnBg: isLight
      ? "linear-gradient(180deg,#f8fbff,#eef5ff)"
      : "linear-gradient(180deg, rgba(24,40,64,0.96), rgba(12,24,42,0.96))",
    managerBtnText: isLight ? "#315a84" : "#dce9fb",
    managerBtnBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(111,168,255,0.18)",

    statusBg: isLight ? "rgba(42,127,255,0.06)" : "rgba(83,167,255,0.10)",
    statusText: isLight ? "#1f63d9" : "#9ec9ff",
    statusBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(83,167,255,0.14)",

    msgCardBg: isLight
      ? "linear-gradient(180deg,#ffffff,#f8fbff)"
      : "linear-gradient(180deg, rgba(15,32,58,0.98), rgba(10,24,43,0.98))",
    msgBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(123,186,255,0.18)",
    msgShadow: isLight
      ? "0 10px 24px rgba(22,47,79,0.08)"
      : "0 16px 30px rgba(0,0,0,0.26), 0 0 0 1px rgba(89,154,255,0.04)",
    msgHeaderBg: isLight
      ? "linear-gradient(180deg,#fdfefe,#f3f8ff)"
      : "linear-gradient(180deg, rgba(24,48,82,0.98), rgba(15,34,60,0.97))",
    msgTitle: isLight ? "#17314b" : "#f4f8ff",
    msgSub: isLight ? "#69809b" : "#a9bdd3",
    msgText: isLight ? "#243b53" : "#d9e6f5",
    emptyText: isLight ? "#7a8da3" : "#8ea3bc",
  };

  return {
    page: {
      position: "relative",
      padding: 0,
      maxWidth: "100%",
      margin: 0,
      fontFamily: "Inter, Arial, sans-serif",
      color: c.pageText,
      background: "transparent",
    },

    surface: {
      width: "100%",
      borderRadius: 30,
      padding: "14px 10px 22px",
      background: c.shellBg,
      border: `1px solid ${c.shellBorder}`,
      boxShadow: c.shellShadow,
      overflow: "hidden",
      position: "relative",
    },

    hero: {
      position: "relative",
      padding: "22px 20px 20px",
      marginBottom: 16,
      borderRadius: 24,
      background: c.heroBg,
      border: `1px solid ${c.heroBorder}`,
      boxShadow: c.heroShadow,
      overflow: "hidden",
    },

    heroGlow: {
      position: "absolute",
      left: "50%",
      bottom: -28,
      transform: "translateX(-50%)",
      width: 320,
      height: 130,
      background: isLight
        ? "radial-gradient(circle, rgba(42,127,255,0.12), transparent 68%)"
        : "radial-gradient(circle, rgba(52,145,255,0.28), transparent 68%)",
      filter: "blur(12px)",
      pointerEvents: "none",
    },

    welcomeEyebrow: {
      marginBottom: 8,
      fontSize: 13,
      fontWeight: 800,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: c.welcome,
    },

    welcomeNeo: {
      color: "#3491ff",
      fontSize: 16,
      fontWeight: 900,
      letterSpacing: "0.08em",
    },

    heroBadge: {
      position: "relative",
      zIndex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "7px 14px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: c.heroBadgeText,
      background: c.heroBadgeBg,
      border: `1px solid ${c.heroBadgeBorder}`,
      boxShadow: isLight
        ? "0 0 18px rgba(42,127,255,0.08)"
        : "0 0 24px rgba(47,128,237,0.12)",
      marginBottom: 18,
      width: "fit-content",
      marginInline: "auto",
    },

    heroTopRow: {
      position: "relative",
      zIndex: 1,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 18,
      alignItems: "center",
    },

    heroLeft: {
      minWidth: 0,
    },

    heroRight: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 8,
      minWidth: 140,
    },

    title: {
      margin: 0,
      fontSize: "clamp(2rem, 5vw, 3.35rem)",
      lineHeight: 1.04,
      fontWeight: 900,
      letterSpacing: "-0.045em",
      color: c.titleMain,
    },

    subtitle: {
      marginTop: 8,
      fontSize: 15,
      color: c.subtitle,
      maxWidth: 620,
    },

    liveClock: {
      fontSize: "clamp(1.8rem, 4vw, 2.7rem)",
      fontWeight: 900,
      color: c.timeText,
      letterSpacing: "-0.03em",
      lineHeight: 1,
    },

    liveDate: {
      fontSize: 14,
      color: c.dateText,
      textAlign: "right",
    },

    netPillOnline: {
      background: c.onlineBg,
      color: c.onlineText,
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      border: `1px solid ${c.onlineBorder}`,
    },

    netPillOffline: {
      background: c.offlineBg,
      color: c.offlineText,
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      border: `1px solid ${c.offlineBorder}`,
    },

    mainCard: {
      marginTop: 6,
      padding: 18,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: 22,
      background: c.cardBg,
      boxShadow: c.cardShadow,
      marginBottom: 18,
    },

    formGrid: {
      display: "grid",
      gap: 12,
      alignItems: "start",
    },

    clockPanel: {
      marginTop: 16,
      paddingTop: 16,
      borderTop: `1px solid ${c.cardBorder}`,
      display: "grid",
      gap: 14,
    },

    clockPanelTitle: {
      fontSize: 16,
      fontWeight: 800,
      color: c.fieldLabel,
    },

    fieldBlock: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },

    label: {
      fontWeight: 700,
      color: c.fieldLabel,
      fontSize: 14,
    },

    hint: {
      fontSize: 12,
      color: c.hint,
    },

    input: {
      padding: 14,
      fontSize: 16,
      borderRadius: 14,
      border: `1px solid ${c.inputBorder}`,
      background: c.inputBg,
      outline: "none",
      color: c.inputText,
      boxShadow: c.inputShadow,
    },

    selectInput: {
      padding: 14,
      fontSize: 16,
      borderRadius: 14,
      border: `1px solid ${c.inputBorder}`,
      background: c.inputBg,
      outline: "none",
      color: c.inputText,
      boxShadow: c.inputShadow,
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
    },

    selectOption: {
      background: isLight ? "#ffffff" : "#09192d",
      color: isLight ? "#17314b" : "#eef5ff",
    },

    readOnlyField: {
      minHeight: 49,
      display: "flex",
      alignItems: "center",
      padding: "0 14px",
      fontSize: 16,
      borderRadius: 14,
      border: `1px solid ${c.inputBorder}`,
      background: c.inputBg,
      color: c.inputText,
      boxShadow: c.inputShadow,
    },

    clockActionBlock: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },

    securityPhotoBlock: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      marginTop: 4,
    },

    shiftPreviewCard: {
      marginTop: 16,
      padding: 16,
      borderRadius: 18,
      border: `1px solid ${c.cardBorder}`,
      background: isLight ? "rgba(42,127,255,0.035)" : "rgba(5,20,38,0.42)",
    },

    shiftPreviewHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 12,
      flexWrap: "wrap",
    },

    shiftPreviewEyebrow: {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.10em",
      textTransform: "uppercase",
      color: c.heroBadgeText,
      marginBottom: 4,
    },

    shiftPreviewTitle: {
      fontSize: 18,
      fontWeight: 900,
      color: c.fieldLabel,
    },

    shiftPreviewGrid: {
      display: "grid",
      gap: 10,
    },

    shiftPreviewItem: {
      padding: "13px 14px",
      borderRadius: 14,
      border: `1px solid ${c.inputBorder}`,
      background: c.inputBg,
      minWidth: 0,
    },

    shiftDay: {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: c.hint,
      marginBottom: 5,
    },

    shiftValue: {
      fontSize: 16,
      fontWeight: 900,
      color: c.inputText,
      lineHeight: 1.25,
    },

    shiftMeta: {
      minHeight: 16,
      marginTop: 4,
      fontSize: 12,
      color: c.hint,
    },

    viewPortalBtn: {
      padding: "10px 14px",
      borderRadius: 12,
      border: `1px solid ${c.scheduleBtnBorder}`,
      background: c.scheduleBtnBg,
      color: c.scheduleBtnText,
      boxShadow: c.scheduleBtnShadow,
      fontWeight: 800,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },

    primaryActionBtn: {
      width: "100%",
      minHeight: 49,
      padding: "13px 16px",
      fontSize: 18,
      borderRadius: 18,
      border: "none",
      cursor: "pointer",
      fontWeight: 900,
      color: "#fff",
      letterSpacing: "-0.01em",
    },

    primaryActionBtnDisabled: {
      cursor: "not-allowed",
      opacity: 0.72,
      filter: "saturate(0.85)",
      boxShadow: "none",
    },

    buttonLoadingContent: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      width: "100%",
    },

    buttonSpinner: {
      width: 18,
      height: 18,
      borderRadius: "50%",
      border: "2px solid rgba(255,255,255,0.45)",
      borderTopColor: "#ffffff",
      display: "inline-block",
      animation: "neoClockButtonSpin 0.8s linear infinite",
    },

    clockInBtn: {
      background: c.clockInBg,
      boxShadow: c.clockInShadow,
    },

    clockOutBtn: {
      background: c.clockOutBg,
      boxShadow: c.clockOutShadow,
    },

    quickActionsLabel: {
      marginTop: 2,
      marginBottom: 10,
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: c.msgSub,
      opacity: 0.8,
    },

    quickActionsGrid: {
      marginTop: 2,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
    },

    secondaryBtn: {
      width: "100%",
      padding: 14,
      borderRadius: 16,
      border: "none",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 16,
      color: "#fff",
      background: c.secondaryBtnBg,
      boxShadow: c.secondaryBtnShadow,
    },

    secondaryBtnDanger: {
      width: "100%",
      padding: 14,
      borderRadius: 16,
      border: "none",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 16,
      color: "#fff",
      background: c.clockOutBg,
      boxShadow: c.clockOutShadow,
    },

    secondaryBtnAlt: {
      width: "100%",
      padding: 14,
      borderRadius: 16,
      border: `1px solid ${c.secondaryBtnAltBorder}`,
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 16,
      color: c.secondaryBtnAltText,
      background: c.secondaryBtnAltBg,
      boxShadow: c.secondaryBtnAltShadow,
    },

    managerActionsGrid: {
      marginTop: 12,
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 12,
    },

    managerBtn: {
      width: "100%",
      padding: 13,
      borderRadius: 14,
      border: `1px solid ${c.managerBtnBorder}`,
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 15,
      color: c.managerBtnText,
      background: c.managerBtnBg,
    },

    refreshBtn: {
      width: "100%",
      padding: 13,
      borderRadius: 14,
      border: `1px solid ${c.managerBtnBorder}`,
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 15,
      color: c.managerBtnText,
      background: c.managerBtnBg,
    },

    statusLine: {
      marginTop: 16,
      textAlign: "center",
      fontWeight: 800,
      color: c.statusText,
      background: c.statusBg,
      border: `1px solid ${c.statusBorder}`,
      borderRadius: 14,
      padding: "10px 12px",
    },

    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: isLight ? "rgba(15, 23, 42, 0.24)" : "rgba(2, 8, 18, 0.62)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      zIndex: 60,
    },

    modalCard: {
      width: "min(420px, calc(100vw - 32px))",
      borderRadius: 18,
      padding: "22px 20px 16px",
      background: isLight
        ? "linear-gradient(180deg, #ffffff, #f7fbff)"
        : "linear-gradient(180deg, rgba(20,40,68,0.98), rgba(12,30,54,0.98))",
      border: isLight
        ? "1px solid rgba(42,127,255,0.12)"
        : "1px solid rgba(123,186,255,0.22)",
      boxShadow: isLight
        ? "0 18px 40px rgba(24,55,96,0.16)"
        : "0 22px 48px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.05)",
      textAlign: "center",
    },

    modalTitle: {
      fontSize: 24,
      fontWeight: 900,
      color: isLight ? "#16324e" : "#f4f8ff",
      letterSpacing: "-0.02em",
      marginBottom: 10,
    },

    modalMessage: {
      fontSize: 17,
      lineHeight: 1.45,
      color: isLight ? "#4e6480" : "#c9d6ea",
      marginBottom: 18,
    },

    modalActions: {
      display: "flex",
      justifyContent: "center",
      gap: 10,
      flexWrap: "wrap",
    },

    modalCancelBtn: {
      minWidth: 96,
      padding: "11px 18px",
      borderRadius: 12,
      border: isLight
        ? "1px solid rgba(42,127,255,0.10)"
        : "1px solid rgba(123,186,255,0.16)",
      background: isLight
        ? "linear-gradient(180deg, #eef4fb, #e2ebf7)"
        : "linear-gradient(180deg, rgba(42,58,80,1), rgba(31,44,62,1))",
      color: isLight ? "#304a69" : "#ffffff",
      fontWeight: 800,
      fontSize: 15,
      cursor: "pointer",
    },

    modalConfirmBtn: {
      minWidth: 86,
      padding: "11px 18px",
      borderRadius: 12,
      border: "none",
      background: isLight
        ? "linear-gradient(180deg, #ef4444, #dc2626)"
        : "linear-gradient(180deg, #ef4444, #b91c1c)",
      color: "#ffffff",
      fontWeight: 900,
      fontSize: 15,
      cursor: "pointer",
      boxShadow: isLight
        ? "0 10px 20px rgba(220,38,38,0.16)"
        : "0 12px 24px rgba(185,28,28,0.24)",
    },

    modalBtnDisabled: {
      cursor: "not-allowed",
      opacity: 0.7,
      boxShadow: "none",
    },

    dashboardQuickBtn: {
      border: isLight ? "1px solid #f59e0b" : "1px solid rgba(245,158,11,.48)",
      background: isLight
        ? "linear-gradient(180deg, #fbbf24, #f59e0b)"
        : "linear-gradient(180deg, rgba(245,158,11,.96), rgba(217,119,6,.96))",
      color: "#111827",
      borderRadius: 16,
      padding: "15px 18px",
      fontWeight: 900,
      fontSize: 17,
      cursor: "pointer",
      boxShadow: isLight
        ? "0 10px 24px rgba(245,158,11,.18)"
        : "0 10px 28px rgba(245,158,11,.16)",
    },

    scheduleOverlay: {
      position: "fixed",
      inset: 0,
      zIndex: 1260,
      background: "rgba(2,8,23,.66)",
      backdropFilter: "blur(5px)",
      display: "flex",
      justifyContent: "flex-end",
    },
    scheduleDrawer: {
      width: "min(640px, 100%)",
      height: "100%",
      overflowY: "auto",
      background: isLight ? "#f7fbff" : "#08182d",
      borderLeft: isLight
        ? "1px solid rgba(5,150,105,.14)"
        : "1px solid rgba(52,211,153,.22)",
      boxShadow: "-24px 0 60px rgba(2,8,23,.28)",
      padding: 24,
      color: c.pageText,
    },
    scheduleDrawerHeader: {
      display: "flex",
      justifyContent: "space-between",
      gap: 16,
      alignItems: "flex-start",
    },
    scheduleDrawerTitle: {
      marginTop: 6,
      fontSize: 28,
      lineHeight: 1.15,
      fontWeight: 950,
      color: c.pageText,
    },
    scheduleDrawerSub: {
      marginTop: 8,
      fontSize: 14,
      color: isLight ? "#64748b" : "#94a3b8",
    },
    scheduleCloseBtn: {
      width: 42,
      height: 42,
      borderRadius: 14,
      border: "1px solid rgba(148,163,184,.22)",
      background: "transparent",
      color: c.pageText,
      fontSize: 24,
      cursor: "pointer",
      flex: "0 0 auto",
    },
    scheduleMonthHeading: {
      marginTop: 22,
      fontSize: 18,
      fontWeight: 950,
      color: c.pageText,
    },
    scheduleMonthIntelCard: {
      marginTop: 14,
      borderRadius: 18,
      border: "1px solid rgba(96,165,250,.22)",
      background: isLight ? "#f8fbff" : "rgba(7,24,45,.82)",
      padding: 16,
    },
    scheduleMonthIntelHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
    },
    scheduleMonthIntelEyebrow: {
      fontSize: 10,
      fontWeight: 900,
      letterSpacing: ".10em",
      color: isLight ? "#2563eb" : "#60a5fa",
    },
    scheduleMonthIntelTitle: {
      marginTop: 5,
      fontSize: 17,
      fontWeight: 950,
      color: c.pageText,
    },
    scheduleMonthIntelVariance: {
      fontSize: 14,
      fontWeight: 950,
      textAlign: "right",
      whiteSpace: "nowrap",
    },
    scheduleMonthIntelGrid: {
      marginTop: 14,
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 8,
    },
    scheduleMonthIntelStat: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      minWidth: 0,
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid rgba(148,163,184,.14)",
      background: isLight ? "#ffffff" : "rgba(15,35,62,.56)",
      fontSize: 11,
      color: isLight ? "#64748b" : "#94a3b8",
    },
    scheduleMonthIntelNote: {
      marginTop: 11,
      fontSize: 11,
      lineHeight: 1.4,
      color: isLight ? "#64748b" : "#94a3b8",
    },

    scheduleMonthSection: {
      marginTop: 14,
    },
    scheduleMonthSectionLabel: {
      marginBottom: 8,
      fontSize: 10,
      fontWeight: 900,
      letterSpacing: ".10em",
      textTransform: "uppercase",
      color: isLight ? "#64748b" : "#94a3b8",
    },
    scheduleMonthGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 8,
    },
    scheduleMonthDayBtn: {
      minWidth: 0,
      minHeight: 86,
      borderRadius: 14,
      border: "1px solid rgba(148,163,184,.18)",
      background: isLight ? "#ffffff" : "rgba(15,35,62,.72)",
      color: c.pageText,
      padding: "9px 10px",
      cursor: "pointer",
      fontWeight: 900,
      textAlign: "left",
    },
    scheduleDateBtnSelected: {
      border: "1px solid rgba(52,211,153,.72)",
      background: "rgba(52,211,153,.14)",
      color: isLight ? "#047857" : "#6ee7b7",
      boxShadow: "0 0 0 1px rgba(52,211,153,.12), 0 8px 18px rgba(16,185,129,.08)",
    },
    scheduleMonthDayToday: {
      outline: "1px solid rgba(52,211,153,.38)",
      outlineOffset: 1,
    },
    scheduleMonthDayOff: {
      background: isLight ? "#fffaf0" : "rgba(120,53,15,.18)",
    },
    scheduleMonthDayLeave: {
      background: isLight ? "#f5f3ff" : "rgba(76,29,149,.18)",
    },
    scheduleDateDow: {
      fontSize: 9,
      letterSpacing: ".08em",
      textTransform: "uppercase",
      opacity: .68,
    },
    scheduleMonthDayTop: {
      marginTop: 2,
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 6,
    },
    scheduleDateNum: {
      fontSize: 18,
      lineHeight: 1.1,
    },
    scheduleToday: {
      fontSize: 8,
      color: isLight ? "#047857" : "#6ee7b7",
    },
    scheduleMonthShiftText: {
      marginTop: 7,
      fontSize: 10,
      lineHeight: 1.25,
      fontWeight: 850,
      overflowWrap: "anywhere",
      opacity: .88,
    },
    scheduleDayPanel: {
      marginTop: 18,
    },
    scheduleDayPanelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 12,
    },
    scheduleBackBtn: {
      border: "1px solid rgba(96,165,250,.28)",
      background: isLight ? "#eff6ff" : "rgba(30,64,175,.16)",
      color: isLight ? "#1d4ed8" : "#93c5fd",
      borderRadius: 12,
      padding: "9px 12px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
    },
    scheduleSelectedCard: {
      marginTop: 12,
      padding: "14px 16px",
      borderRadius: 16,
      border: "1px solid rgba(148,163,184,.16)",
      background: isLight ? "#ffffff" : "rgba(15,35,62,.68)",
    },
    scheduleSelectedTitle: {
      marginTop: 5,
      fontSize: 18,
      fontWeight: 900,
    },
    scheduleStaffCard: {
      marginTop: 14,
      border: "1px solid rgba(148,163,184,.16)",
      background: isLight ? "#ffffff" : "rgba(15,35,62,.72)",
      borderRadius: 18,
      padding: 16,
      display: "flex",
      justifyContent: "space-between",
      gap: 14,
      alignItems: "center",
    },
    scheduleStaffName: {
      fontSize: 18,
      fontWeight: 900,
    },
    scheduleStaffRole: {
      marginTop: 4,
      fontSize: 12,
      opacity: .72,
    },
    scheduleShiftValue: {
      textAlign: "right",
      fontSize: 16,
      fontWeight: 900,
      color: isLight ? "#0369a1" : "#67e8f9",
    },
    scheduleShiftOff: {
      color: "#fbbf24",
    },
    scheduleIntelligenceCard: {
      marginTop: 12,
      border: "1px solid rgba(96,165,250,.22)",
      background: isLight ? "#f8fbff" : "rgba(7,24,45,.78)",
      borderRadius: 18,
      padding: 16,
    },
    scheduleIntelligenceHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
    },
    scheduleIntelligenceTitle: {
      marginTop: 4,
      fontSize: 19,
      fontWeight: 950,
      color: c.pageText,
    },
    scheduleStatusPill: {
      maxWidth: "52%",
      border: "1px solid",
      borderRadius: 999,
      padding: "7px 10px",
      fontSize: 11,
      lineHeight: 1.25,
      fontWeight: 900,
      textAlign: "right",
    },
    scheduleIntelligenceGrid: {
      marginTop: 14,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
    },
    scheduleIntelBlock: {
      minWidth: 0,
      border: "1px solid rgba(148,163,184,.14)",
      borderRadius: 14,
      padding: 12,
      background: isLight ? "#ffffff" : "rgba(15,35,62,.56)",
    },
    scheduleIntelLabel: {
      fontSize: 9,
      fontWeight: 900,
      letterSpacing: ".10em",
      color: isLight ? "#64748b" : "#93c5fd",
    },
    scheduleIntelValue: {
      marginTop: 7,
      fontSize: 15,
      fontWeight: 900,
      color: c.pageText,
      overflowWrap: "anywhere",
    },
    scheduleIntelSub: {
      marginTop: 5,
      fontSize: 11,
      color: isLight ? "#64748b" : "#94a3b8",
      lineHeight: 1.35,
    },
    scheduleDecisionRow: {
      marginTop: 12,
      paddingTop: 12,
      borderTop: "1px solid rgba(148,163,184,.14)",
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      alignItems: "center",
      fontSize: 11,
      color: isLight ? "#64748b" : "#94a3b8",
    },
    scheduleIssueCard: {
      marginTop: 12,
      border: "1px solid rgba(52,211,153,.22)",
      background: isLight ? "#f7fffb" : "rgba(6,78,59,.12)",
      borderRadius: 18,
      padding: 16,
    },
    scheduleIssueHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
    },
    scheduleIssueTitle: {
      marginTop: 5,
      fontSize: 16,
      fontWeight: 950,
      color: c.pageText,
    },
    scheduleIssueCopy: {
      marginTop: 8,
      fontSize: 12,
      lineHeight: 1.45,
      color: isLight ? "#64748b" : "#94a3b8",
    },
    scheduleIssueBtn: {
      marginTop: 12,
      width: "100%",
      border: "1px solid rgba(52,211,153,.32)",
      background: isLight ? "#ecfdf5" : "rgba(16,185,129,.14)",
      color: isLight ? "#047857" : "#6ee7b7",
      borderRadius: 12,
      padding: "11px 12px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
    },
    scheduleIssueSent: {
      borderRadius: 999,
      padding: "6px 9px",
      background: "rgba(52,211,153,.14)",
      border: "1px solid rgba(52,211,153,.28)",
      color: isLight ? "#047857" : "#6ee7b7",
      fontSize: 10,
      fontWeight: 900,
    },
    scheduleIssueForm: {
      marginTop: 12,
      display: "grid",
      gap: 10,
    },
    scheduleIssueLabel: {
      display: "grid",
      gap: 6,
      fontSize: 11,
      fontWeight: 800,
      color: isLight ? "#475569" : "#cbd5e1",
    },
    scheduleIssueSelect: {
      width: "100%",
      borderRadius: 12,
      border: "1px solid rgba(148,163,184,.22)",
      background: isLight ? "#ffffff" : "rgba(15,35,62,.84)",
      color: c.pageText,
      padding: "11px 12px",
      fontSize: 12,
    },
    scheduleIssueTextarea: {
      width: "100%",
      boxSizing: "border-box",
      resize: "vertical",
      borderRadius: 12,
      border: "1px solid rgba(148,163,184,.22)",
      background: isLight ? "#ffffff" : "rgba(15,35,62,.84)",
      color: c.pageText,
      padding: "11px 12px",
      fontSize: 12,
      fontFamily: "inherit",
    },
    scheduleIssueActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
    },
    scheduleIssueCancelBtn: {
      borderRadius: 11,
      border: "1px solid rgba(148,163,184,.22)",
      background: "transparent",
      color: c.pageText,
      padding: "9px 11px",
      fontSize: 11,
      fontWeight: 850,
      cursor: "pointer",
    },
    scheduleIssueSubmitBtn: {
      borderRadius: 11,
      border: "1px solid rgba(52,211,153,.34)",
      background: "rgba(16,185,129,.18)",
      color: isLight ? "#047857" : "#6ee7b7",
      padding: "9px 12px",
      fontSize: 11,
      fontWeight: 900,
      cursor: "pointer",
    },
    scheduleHint: {
      marginTop: 18,
      padding: 14,
      borderRadius: 16,
      border: "1px dashed rgba(52,211,153,.28)",
      color: isLight ? "#047857" : "#a7f3d0",
      fontSize: 12,
      lineHeight: 1.5,
    },
    scheduleEmpty: {
      marginTop: 22,
      padding: 24,
      textAlign: "center",
      borderRadius: 16,
      border: "1px dashed rgba(148,163,184,.18)",
      color: isLight ? "#64748b" : "#94a3b8",
    },

    msgCard: {
      marginTop: 8,
      border: `1px solid ${c.msgBorder}`,
      borderRadius: 22,
      overflow: "hidden",
      background: c.msgCardBg,
      boxShadow: c.msgShadow,
      color: c.pageText,
    },

    msgHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "14px 16px",
      background: c.msgHeaderBg,
      borderBottom: `1px solid ${c.msgBorder}`,
    },

    msgTitle: {
      fontWeight: 800,
      color: c.msgTitle,
      fontSize: 15,
      letterSpacing: "0.2px",
    },

    msgSub: {
      fontSize: 12,
      color: c.msgSub,
      marginTop: 2,
    },

    msgBody: {
      padding: 16,
    },

    messageText: {
      whiteSpace: "pre-wrap",
      lineHeight: 1.65,
      color: c.msgText,
      fontSize: 15,
    },

    emptyMessage: {
      color: c.emptyText,
    },

    matrixFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 8,
      padding: "16px 12px 2px",
      textAlign: "center",
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.4,
    },

    matrixFooterPowered: {
      color: isLight ? "#526d8c" : "#8fb8e8",
    },

    matrixFooterDot: {
      color: isLight ? "#2a7fff" : "#54a0ff",
    },

    matrixFooterTagline: {
      color: isLight ? "#1f63d9" : "#54a0ff",
    },

    photoCaptureBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 48,
      padding: "0 16px",
      borderRadius: 14,
      border: `1px solid ${c.secondaryBtnAltBorder}`,
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 15,
      color: c.secondaryBtnAltText,
      background: c.secondaryBtnAltBg,
      boxShadow: c.secondaryBtnAltShadow,
      width: "fit-content",
    },

    photoCaptureReadyBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 48,
      padding: "0 16px",
      borderRadius: 14,
      border: "1px solid rgba(42,127,255,0.28)",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 15,
      color: isLight ? "#1f63d9" : "#9ec9ff",
      background: isLight
        ? "linear-gradient(180deg, rgba(42,127,255,0.10), rgba(31,99,217,0.08))"
        : "linear-gradient(180deg, rgba(45,140,255,0.18), rgba(31,111,224,0.12))",
      boxShadow: isLight
        ? "0 8px 16px rgba(31,99,217,0.10)"
        : "0 10px 20px rgba(47,128,237,0.14)",
      width: "fit-content",
    },

    photoCaptureDisabledBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 48,
      padding: "0 16px",
      borderRadius: 14,
      border: isLight
        ? "1px solid rgba(120,138,160,0.18)"
        : "1px solid rgba(120,138,160,0.22)",
      cursor: "not-allowed",
      fontWeight: 800,
      fontSize: 15,
      color: isLight ? "#8a99ab" : "#7f93aa",
      background: isLight
        ? "linear-gradient(180deg, #f3f6fa, #eef3f8)"
        : "linear-gradient(180deg, rgba(28,39,55,0.96), rgba(18,28,42,0.96))",
      boxShadow: "none",
      width: "fit-content",
      opacity: 0.9,
    },

    photoCaptureDoneBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 48,
      padding: "0 16px",
      borderRadius: 14,
      border: "1px solid rgba(32,183,109,0.28)",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 15,
      color: isLight ? "#14995a" : "#6ef0b1",
      background: isLight
        ? "linear-gradient(180deg, rgba(32,183,109,0.10), rgba(20,153,90,0.08))"
        : "linear-gradient(180deg, rgba(32,183,109,0.14), rgba(20,153,90,0.10))",
      boxShadow: isLight
        ? "0 8px 16px rgba(20,153,90,0.10)"
        : "0 10px 20px rgba(20,153,90,0.14)",
      width: "fit-content",
    },

    photoPreviewWrap: {
      marginTop: 8,
      width: 120,
      height: 120,
      borderRadius: 16,
      overflow: "hidden",
      border: `1px solid ${c.inputBorder}`,
      background: c.inputBg,
      boxShadow: c.inputShadow,
    },

    photoPreview: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
  };
};